import React, { useState, useEffect, useRef } from 'react';
import { useColumnCustomizer } from '../components/ColumnCustomizer';
import ChipOverflowStrip from '../components/ChipOverflowStrip';
import PillTabs from '../components/PillTabs';
import { usePermissions } from '../hooks/usePermissions';

const TASK_COLUMNS = [
  { key: 'task_number',  label: 'Task ID' },
  { key: 'title',        label: 'Service / Task' },
  { key: 'client_name',  label: 'Client' },
  { key: 'assigned',     label: 'Assigned To' },
  { key: 'priority',     label: 'Priority' },
  { key: 'due_date',     label: 'Due Date' },
  { key: 'target_date',  label: 'Target Date' },
  { key: 'progress',     label: 'Progress' },
  { key: 'status',       label: 'Status' },
  { key: 'tags',         label: 'Tags' },
];

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const STATUS_COLORS = {
  pending:              'bg-amber-50 text-amber-700 border border-amber-200',
  in_progress:          'bg-blue-50 text-blue-700 border border-blue-200',
  hold:                 'bg-orange-50 text-orange-700 border border-orange-200',
  sent_for_approval:    'bg-purple-50 text-purple-700 border border-purple-200',
  escalated:            'bg-pink-50 text-pink-700 border border-pink-200',
  approved:             'bg-teal-50 text-teal-700 border border-teal-200',
  completed:            'bg-green-50 text-green-700 border border-green-200',
  cancelled:            'bg-red-50 text-red-700 border border-red-200',
};
const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700'
};
const STATUS_LABELS = {
  pending:           'Pending',
  in_progress:       'In Progress',
  hold:              'On Hold',
  sent_for_approval: 'Sent for Approval',
  escalated:         'Escalated to Admin',
  approved:          'Approved',
  completed:         'Completed',
  cancelled:         'Cancelled',
};

// Point 2: Smart progress calculation
function getProgress(task) {
  const checkTotal = parseInt(task.checklist_total) || 0;
  const checkDone = parseInt(task.checklist_done) || 0;
  const subTotal = parseInt(task.subtask_total) || 0;
  const subDone = parseInt(task.subtask_done) || 0;
  const childTotal = parseInt(task.child_task_total) || 0;
  const childDone = parseInt(task.child_task_done) || 0;
  const total = checkTotal + subTotal + childTotal;
  const done = checkDone + subDone + childDone;

  if (total > 0) return { pct: Math.round((done / total) * 100), label: `${done}/${total}`, hasBasis: true };
  if (task.status === 'completed') return { pct: 100, label: 'Done', hasBasis: false };
  if (task.status === 'cancelled') return { pct: 0, label: 'Cancelled', hasBasis: false };
  return { pct: 0, label: '0%', hasBasis: false };
}

function SmartDatePicker({ label, value, onChange, placeholder = 'dd/mm/yyyy', darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => value ? new Date(value).getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? new Date(value).getMonth() : new Date().getMonth());
  const [mode, setMode] = useState('day');
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  const today  = new Date(); today.setHours(0,0,0,0);
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const getDays = () => {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prev  = new Date(viewYear, viewMonth, 0).getDate();
    const cells = [];
    for (let i = first-1; i >= 0; i--) cells.push({ d: prev-i, curr: false });
    for (let d = 1; d <= total; d++)    cells.push({ d, curr: true });
    while (cells.length % 7 !== 0)      cells.push({ d: cells.length - first - total + 1, curr: false });
    return cells;
  };
  const selectDay = d => {
    const m = String(viewMonth+1).padStart(2,'0'), dd = String(d).padStart(2,'0');
    onChange(`${viewYear}-${m}-${dd}`); setOpen(false);
  };
  const yearRange = Array.from({ length: 12 }, (_, i) => viewYear - 5 + i);
  const displayVal = value ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';
  return (
    <div style={{ position:'relative' }} ref={ref}>
      {label && <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</label>}
      <button type="button" onClick={() => { setOpen(o => !o); setMode('day'); }}
        style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', background:T.inputBg, cursor:'pointer', outline:'none', boxSizing:'border-box' }}>
        <span style={{ color: displayVal ? T.text : T.text3 }}>{displayVal || placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.12)', zIndex:9999, width:288, overflow:'hidden' }}>
          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:`1px solid ${T.border}` }}>
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===0){setViewYear(y=>y-1);return 11;} return m-1; }); else if(mode==='year') setViewYear(y=>y-12); }}
              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:T.text2 }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg3}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <button type="button" onClick={() => setMode(m => m==='month'?'day':'month')}
                style={{ padding:'4px 8px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:700, color:T.text, cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                onMouseLeave={e => e.currentTarget.style.background='none'}>
                {MONTHS[viewMonth]}
              </button>
              <button type="button" onClick={() => setMode(m => m==='year'?'day':'year')}
                style={{ padding:'4px 8px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:700, color:T.text, cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                onMouseLeave={e => e.currentTarget.style.background='none'}>
                {viewYear}
              </button>
            </div>
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===11){setViewYear(y=>y+1);return 0;} return m+1; }); else if(mode==='year') setViewYear(y=>y+12); }}
              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:T.text2 }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg3}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          {/* Month picker */}
          {mode==='month' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, padding:12 }}>
              {MONTHS.map((m,i) => (
                <button key={m} type="button" onClick={() => { setViewMonth(i); setMode('day'); }}
                  style={{ padding:'8px 0', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer', background: viewMonth===i?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none', color: viewMonth===i?'#fff':T.text2, transition:'all 0.15s' }}
                  onMouseEnter={e => { if(viewMonth!==i) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e => { if(viewMonth!==i) e.currentTarget.style.background='none'; }}>
                  {m}
                </button>
              ))}
            </div>
          )}

          {/* Year picker */}
          {mode==='year' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, padding:12 }}>
              {yearRange.map(y => (
                <button key={y} type="button" onClick={() => { setViewYear(y); setMode('day'); }}
                  style={{ padding:'8px 0', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer', background: viewYear===y?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none', color: viewYear===y?'#fff':T.text2, transition:'all 0.15s' }}
                  onMouseEnter={e => { if(viewYear!==y) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e => { if(viewYear!==y) e.currentTarget.style.background='none'; }}>
                  {y}
                </button>
              ))}
            </div>
          )}

          {/* Day picker */}
          {mode==='day' && (
            <div style={{ padding:12 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:4 }}>
                {DAYS.map(d => <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:600, color:T.text3, padding:'4px 0' }}>{d}</div>)}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
                {getDays().map((cell,i) => {
                  const cd = new Date(viewYear, viewMonth, cell.d); cd.setHours(0,0,0,0);
                  const isSel = selected && cell.curr && cd.getTime()===selected.getTime();
                  const isToday = cell.curr && cd.getTime()===today.getTime();
                  return (
                    <button key={i} type="button" onClick={() => cell.curr && selectDay(cell.d)}
                      style={{
                        height:32, width:'100%', borderRadius:8, border: isToday&&!isSel?`1px solid #3b82f6`:'none',
                        background: isSel?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none',
                        color: !cell.curr?T.text3 : isSel?'#fff' : isToday?'#3b82f6' : T.text2,
                        fontSize:12, fontWeight: isSel||isToday?700:400,
                        cursor: cell.curr?'pointer':'default', transition:'all 0.1s',
                      }}
                      onMouseEnter={e => { if(cell.curr&&!isSel) e.currentTarget.style.background=T.bg3; }}
                      onMouseLeave={e => { if(!isSel) e.currentTarget.style.background='none'; }}>
                      {cell.d}
                    </button>
                  );
                })}
              </div>
              {value && (
                <button type="button" onClick={() => { onChange(''); setOpen(false); }}
                  style={{ width:'100%', marginTop:8, padding:'6px 0', fontSize:12, color:T.text3, background:'none', border:'none', cursor:'pointer', borderRadius:8 }}
                  onMouseEnter={e => { e.currentTarget.style.color='#ef4444'; e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.1)':'#fef2f2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color=T.text3; e.currentTarget.style.background='none'; }}>
                  Clear date
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskAssignModal({ task, users, token, onClose, onSuccess }) {
  const [selected, setSelected] = useState(() => {
    // Initialize from assigned_user_ids (multi) or fall back to assigned_to (single)
    if (task.assigned_user_ids) {
      return task.assigned_user_ids.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    }
    return task.assigned_to ? [Number(task.assigned_to)] : [];
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (selected.length === 0) { alert('Please select at least one user'); return; }
    setSaving(true);
    try {
      await fetch(`${BASE}/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...task,
          assigned_to: selected[0],        // primary assignee (backward compat)
          assigned_users: selected,        // all selected user IDs → saved to junction table
        })
      });
      onSuccess();
      onClose();
    } catch { alert('Error assigning users'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <p className="text-sm font-bold" style={{ color: '#0f1f45' }}>Assign User</p>
            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-64">{task.task_number} · {task.service_name || task.title}</p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {users.length > 0 && (
          <div className="px-5 pt-3 flex items-center justify-between">
            <span className="text-xs text-gray-400">{selected.length} of {users.length} selected</span>
            <button type="button"
              onClick={() => setSelected(prev => prev.length === users.length ? [] : users.map(u => u.id))}
              className="text-xs font-medium text-blue-600 hover:text-blue-800">
              {selected.length === users.length ? 'Clear All' : 'Assign All'}
            </button>
          </div>
        )}

        {/* User list */}
        <div className="p-4 space-y-1.5 max-h-72 overflow-y-auto">
          {users.length === 0 && <p className="text-center text-gray-400 text-sm py-4">No users found</p>}
          {users.map(u => {
            const isSelected = selected.includes(u.id);
            return (
              <label key={u.id}
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition border ${isSelected ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-100 hover:bg-gray-50'}`}>
                <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition"
                  style={isSelected ? { background: '#0f1f45', borderColor: '#0f1f45' } : { borderColor: '#d1d5db' }}>
                  {isSelected && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
                <input type="checkbox" className="hidden" checked={isSelected}
                  onChange={() => setSelected(prev =>
                    prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id]
                  )} />
                <div className="flex items-center gap-2.5 flex-1">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                    style={{ background: '#0f1f45' }}>
                    {u.full_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{u.full_name}</p>
                    <p className="text-xs text-gray-400 capitalize">{u.role}</p>
                  </div>
                </div>
                {isSelected && (
                  <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full flex-shrink-0">
                    Assigned
                  </span>
                )}
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
            style={{ background: '#0f1f45' }}>
            {saving ? 'Saving...' : `Save${selected.length > 0 ? ` (${selected.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reuses the same tags_master used by the Clients module — a tag created here shows up there too, and vice versa.
function TaskTagsModal({ task, allTags, token, onClose, onSuccess, onTagCreated }) {
  const [selected, setSelected] = useState(() =>
    task.tags_data ? task.tags_data.split(',').map(t => parseInt(t.split(':')[0])).filter(id => !isNaN(id)) : []
  );
  const [saving, setSaving] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const TAG_COLORS = ['#ef4444','#f97316','#f59e0b','#84cc16','#10b981','#0d9488','#3b82f6','#6366f1','#8b5cf6','#ec4899'];

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      const color = TAG_COLORS[allTags.length % TAG_COLORS.length];
      const res = await fetch(`${BASE}/api/clients/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newTagName.trim(), color })
      });
      const tag = await res.json();
      onTagCreated(tag);
      setSelected(prev => prev.includes(tag.id) ? prev : [...prev, tag.id]);
      setNewTagName('');
    } catch { alert('Error creating tag'); }
    finally { setCreatingTag(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${BASE}/api/tasks/${task.id}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ tag_ids: selected })
      });
      onSuccess();
      onClose();
    } catch { alert('Error saving tags'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <p className="text-sm font-bold" style={{ color: '#0f1f45' }}>Tags</p>
            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-64">{task.task_number} · {task.service_name || task.title}</p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-1.5 max-h-64 overflow-y-auto">
          {allTags.length === 0 && <p className="text-center text-gray-400 text-sm py-4">No tags yet — create one below</p>}
          {allTags.map(tag => {
            const isSelected = selected.includes(tag.id);
            return (
              <label key={tag.id}
                className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition border ${isSelected ? 'border-gray-200' : 'bg-white border-gray-100 hover:bg-gray-50'}`}
                style={isSelected ? { background: (tag.color||'#6366f1') + '14' } : {}}>
                <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition"
                  style={isSelected ? { background: tag.color||'#6366f1', borderColor: tag.color||'#6366f1' } : { borderColor: '#d1d5db' }}>
                  {isSelected && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
                <input type="checkbox" className="hidden" checked={isSelected}
                  onChange={() => setSelected(prev => prev.includes(tag.id) ? prev.filter(id => id !== tag.id) : [...prev, tag.id])} />
                <span className="text-sm font-medium" style={{ color: tag.color||'#374151' }}>{tag.name}</span>
              </label>
            );
          })}
        </div>

        <div className="px-4 pb-3 flex gap-2">
          <input type="text" placeholder="New tag name..." value={newTagName}
            onChange={e => setNewTagName(e.target.value)}
            onKeyDown={e => e.key==='Enter' && (e.preventDefault(), handleCreateTag())}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          <button type="button" onClick={handleCreateTag} disabled={creatingTag || !newTagName.trim()}
            className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50 disabled:opacity-40">
            {creatingTag ? '...' : '+ Add'}
          </button>
        </div>

        <div className="px-5 py-4 border-t flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
            style={{ background: '#0f1f45' }}>
            {saving ? 'Saving...' : `Save${selected.length > 0 ? ` (${selected.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ open, title, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:99999, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, width:'min(92vw,420px)', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
            </div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1f45', margin:0 }}>{title}</h3>
          </div>
          <p style={{ fontSize:13, color:'#64748b', margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', gap:10, padding:'16px 24px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#4a5568', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'none', background:'#ef4444', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function Tasks({ darkMode = false, T: theme }) {
  const T = theme || { bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm) => setConfirm({ title, message, onConfirm });
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPrefillClientId, setAddPrefillClientId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTaskType, setFilterTaskType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  // Category filters are multi-select (arrays; empty = all). Dates stay single values.
  const EMPTY_FILTERS = { status: [], priority: [], assigned_to: [], client_id: [], billing_status: [], tags: [], date_from: '', date_to: '', date_preset: '' };
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tagsTask, setTagsTask] = useState(null);
  const cc = useColumnCustomizer(TASK_COLUMNS, { visKey: 'taskVisibleCols', orderKey: 'taskColumnOrder', T, darkMode });
  const { visibleColumns, orderedVisibleKeys } = cc;

  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const [openKebabId, setOpenKebabId] = useState(null);
  const [openKebabUp, setOpenKebabUp] = useState(false);
  const [openKebabRect, setOpenKebabRect] = useState(null);
  const [statusChangeId, setStatusChangeId] = useState(null);
  const [assignTask, setAssignTask] = useState(null);
  // Bulk selection + actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null); // { ok, text }
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkGroupOpen, setBulkGroupOpen] = useState(false);
  // Task groups (Settings → Services & Tasks). groupFilter: 'all' | 'none' | group id
  const [taskGroups, setTaskGroups] = useState([]);
  const [groupFilter, setGroupFilterState] = useState(() => { try { return localStorage.getItem('taskGroupFilter') || 'all'; } catch { return 'all'; } });
  const setGroupFilter = (v) => { setGroupFilterState(v); try { localStorage.setItem('taskGroupFilter', v); } catch {} };
  const [collapsedGroups, setCollapsedGroups] = useState([]);
  const [groupMenuTaskId, setGroupMenuTaskId] = useState(null);
  const GROUP_SWATCHES = ['#10b981','#6366f1','#f97316','#ec4899','#0ea5e9','#8b5cf6','#f59e0b','#64748b'];
  // Task groups follow the role: "Manage task groups" creates them, "Edit tasks" moves tasks between them.
  const { can: permCan } = usePermissions();
  const canManageGroups = permCan('tasks.groups_manage');
  const canMoveGroups = permCan('tasks.edit');
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState(GROUP_SWATCHES[0]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  // Column sorting (click a header: ascending → descending → off) and column
  // widths (drag a header's right edge; double-click it to reset). Both remembered.
  const readLS = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) || d; } catch { return d; } };
  const [sortBy, setSortByState] = useState(() => readLS('taskSort', { key: null, dir: 'asc' }));
  const setSortBy = (v) => { setSortByState(v); try { localStorage.setItem('taskSort', JSON.stringify(v)); } catch {} };
  const [colWidths, setColWidthsState] = useState(() => readLS('taskColWidths', {}));
  const setColWidths = (v) => { setColWidthsState(v); try { localStorage.setItem('taskColWidths', JSON.stringify(v)); } catch {} };
  const justResized = useRef(false);
  const toggleSort = (key) => {
    if (justResized.current) return; // the click that ends a drag isn't a sort
    if (sortBy.key !== key) setSortBy({ key, dir: 'asc' });
    else if (sortBy.dir === 'asc') setSortBy({ key, dir: 'desc' });
    else setSortBy({ key: null, dir: 'asc' });
  };
  const startResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    const th = e.currentTarget.parentElement;
    const startX = e.clientX, startW = th.offsetWidth;
    let latest = colWidths;
    const move = (ev) => {
      latest = { ...colWidths, [key]: Math.max(60, Math.round(startW + ev.clientX - startX)) };
      setColWidthsState(latest);
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      setColWidths(latest);
      justResized.current = true;
      setTimeout(() => { justResized.current = false; }, 0);
    };
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };
  const resetWidth = (e, key) => {
    e.stopPropagation();
    const { [key]: _drop, ...rest } = colWidths;
    setColWidths(rest);
  };
  // The bulk bar only stays while tasks are ticked — a result message fades out on its own.
  useEffect(() => {
    if (!bulkMsg || selectedIds.length) return;
    const t = setTimeout(() => setBulkMsg(null), 5000);
    return () => clearTimeout(t);
  }, [bulkMsg, selectedIds.length]);
  // Close kebab on outside click
  useEffect(() => {
    const h = e => {
      if (!e.target.closest('[data-kebab]')) {
        setOpenKebabId(null); setStatusChangeId(null); setGroupMenuTaskId(null);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const exportToXLSX = () => {
    const data = filtered.map(t => ({
      'Task ID': t.task_number || '',
      'Service / Task': t.service_name || t.title || '',
      'Client': t.client_name || '',
      'File No.': t.file_number || '',
      'Assigned To': t.assigned_name || '',
      'Priority': t.priority || '',
      'Status': STATUS_LABELS[t.status] || t.status,
      'Due Date': t.due_date ? new Date(t.due_date).toLocaleDateString('en-IN') : '',
      'Target Date': t.target_date ? new Date(t.target_date).toLocaleDateString('en-IN') : '',
      'Billing Status': t.billing_status || '',
      'Group': t.task_group_name || 'Ungrouped',
    }));
    const ws = window.XLSX ? window.XLSX.utils.json_to_sheet(data) : null;
    if (!ws) { alert('Please add: import * as XLSX from "xlsx"; at top of file'); return; }
    const wb = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
    window.XLSX.writeFile(wb, `Tasks_${new Date().toLocaleDateString('en-IN').replace(/\//g,'-')}.xlsx`);
  };

  const handleQuickStatus = async (taskId, status) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      await fetch(`${BASE}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...task, status, completed_date: status === 'completed' ? new Date().toISOString().split('T')[0] : null })
      });
      setStatusChangeId(null); setOpenKebabId(null); handleRefresh();
    } catch(e) { alert('Error updating status'); }
  };

  const fetchTasks = async () => {
    try {
      // Point 4: pass type filter to backend
      const typeParam = filterTaskType !== 'all' ? `&type=${filterTaskType}` : '';
      const res = await fetch(`${BASE}/api/tasks?_=${Date.now()}${typeParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTasks(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${BASE}/api/tasks/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setStats(data);
    } catch (e) { console.error(e); }
  };

  const fetchTaskGroups = () => fetch(`${BASE}/api/task-groups`, { headers: { 'Authorization': `Bearer ${token}` } })
    .then(r => r.json()).then(d => setTaskGroups(Array.isArray(d) ? d : [])).catch(() => {});

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchTasks();
    fetchStats();
    fetchUsers();
    fetchTaskGroups();
    fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/clients/tags/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setAllTags(Array.isArray(d) ? d : [])).catch(() => {});
  }, [filterTaskType]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Create Task" from a Client Dashboard → Tasks tab: open the New Task form
  // with that client already selected.
  useEffect(() => {
    const pending = localStorage.getItem('pendingTaskClientId');
    if (!pending) return;
    localStorage.removeItem('pendingTaskClientId');
    setAddPrefillClientId(pending);
    setShowAddModal(true);
  }, []);

  // Deep-link support: when another page (e.g. Client Dashboard → Tasks tab)
  // navigates here via the `navigateTo` event with a taskId, open that
  // task's detail modal directly instead of just landing on the list.
  useEffect(() => {
    if (loading || !tasks.length) return;
    const navTaskId = localStorage.getItem('navTaskId');
    if (!navTaskId) return;
    localStorage.removeItem('navTaskId');
    const task = tasks.find(t => String(t.id) === String(navTaskId));
    if (task) setSelectedTask(task);
  }, [loading, tasks]);

  const applyDatePreset = (preset) => {
    const now = new Date();
    let from = '', to = '';
    if (preset === 'today') { from = to = now.toISOString().split('T')[0]; }
    else if (preset === 'this_week') {
      const day = now.getDay();
      const mon = new Date(now); mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      from = mon.toISOString().split('T')[0]; to = now.toISOString().split('T')[0];
    } else if (preset === 'this_month') {
      from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      to = now.toISOString().split('T')[0];
    }
    setFilters(f => ({ ...f, date_from: from, date_to: to, date_preset: preset }));
  };

  // Every filter except the group — the group strip counts come from this list.
  const beforeGroup = tasks.filter(t => {
    const matchSearch = !searchTerm ||
      t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.task_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.service_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchTab =
      activeTab === 'all' ||
      (activeTab === 'my_tasks' && String(t.assigned_to) === String(currentUser?.id)) ||
      (activeTab === 'overdue' && t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed') ||
      (activeTab === 'due_today' && t.due_date && new Date(t.due_date).toDateString() === new Date().toDateString()) ||
      t.status === activeTab;
    const anyOf = (sel, v) => !sel.length || sel.includes(String(v));
    const matchStatus = anyOf(filters.status, t.status);
    const matchPriority = anyOf(filters.priority, t.priority);
    // any of the task's assignees (multi-assign) counts
    const taskUserIds = String(t.assigned_user_ids || t.assigned_to || '').split(',').map(x => x.trim()).filter(Boolean);
    const matchAssigned = !filters.assigned_to.length || taskUserIds.some(id => filters.assigned_to.includes(id));
    const matchClient = anyOf(filters.client_id, t.client_id);
    const matchBilling = anyOf(filters.billing_status, t.billing_status);
    const taskTagIds = String(t.tags_data || '').split(',').map(x => x.split(':')[0]).filter(Boolean);
    const matchTags = !filters.tags.length || taskTagIds.some(id => filters.tags.includes(id));
    const matchFrom = !filters.date_from || (t.due_date && t.due_date >= filters.date_from);
    const matchTo = !filters.date_to || (t.due_date && t.due_date <= filters.date_to);
    return matchSearch && matchTab && matchStatus && matchPriority && matchAssigned && matchClient && matchBilling && matchTags && matchFrom && matchTo;
  });

  // Sort (inside each group when the list is grouped). Empty values always go last.
  if (sortBy.key) {
    const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };
    const STATUS_RANK = Object.keys(STATUS_LABELS);
    const val = (t) => {
      switch (sortBy.key) {
        case 'task_number': return parseInt(String(t.task_number || '').replace(/\D/g, '')) || null;
        case 'title':       return (t.service_name || t.title || '').toLowerCase() || null;
        case 'client_name': return (t.client_name || '').toLowerCase() || null;
        case 'assigned':    return (t.assigned_names || t.assigned_name || '').toLowerCase() || null;
        case 'priority':    return PRIORITY_RANK[t.priority] ?? null;
        case 'due_date':    return t.due_date ? new Date(t.due_date).getTime() : null;
        case 'target_date': return t.target_date ? new Date(t.target_date).getTime() : null;
        case 'progress':    return getProgress(t).pct;
        case 'status':      return STATUS_RANK.indexOf(t.status);
        case 'tags':        return (t.tags || '').toLowerCase() || null;
        default:            return null;
      }
    };
    const dir = sortBy.dir === 'desc' ? -1 : 1;
    beforeGroup.sort((a, b) => {
      const x = val(a), y = val(b);
      if (x === null && y === null) return 0;
      if (x === null) return 1;
      if (y === null) return -1;
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
  }

  // Task groups: the strip picks one group; on "All" the table shows group
  // header rows (in group order, Ungrouped last) once the firm has groups.
  const groupKey = (t) => t.task_group_id ? String(t.task_group_id) : 'none';
  const groupCounts = beforeGroup.reduce((m, t) => { const k = groupKey(t); m[k] = (m[k] || 0) + 1; return m; }, {});
  const activeGroup = groupFilter === 'all' || groupFilter === 'none' || taskGroups.some(g => String(g.id) === groupFilter) ? groupFilter : 'all';
  const groupOrder = [...taskGroups.map(g => String(g.id)), 'none'];
  const showGroupHeaders = taskGroups.length > 0 && activeGroup === 'all';
  const filtered = activeGroup === 'all'
    ? (showGroupHeaders ? groupOrder.flatMap(k => beforeGroup.filter(t => groupKey(t) === k)) : beforeGroup)
    : beforeGroup.filter(t => groupKey(t) === activeGroup);
  const groupInfo = (k) => k === 'none'
    ? { name: 'Ungrouped', color: '#94a3b8' }
    : (taskGroups.find(g => String(g.id) === k) || { name: 'Group', color: '#94a3b8' });
  // Stats cards follow the selected group: All Groups → the firm-wide server
  // counts; a group → the same counts worked out from that group's tasks
  // (same rules as GET /api/tasks/stats, day-level overdue / due today).
  const dayKey = (d) => { const x = new Date(d); return isNaN(x) ? '' : `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; };
  const cardStats = activeGroup === 'all' ? stats : (() => {
    const today = dayKey(new Date());
    const list = tasks.filter(t => groupKey(t) === activeGroup);
    const open = t => !['completed', 'cancelled'].includes(t.status);
    return {
      total: list.length,
      pending: list.filter(t => t.status === 'pending').length,
      in_progress: list.filter(t => t.status === 'in_progress').length,
      hold: list.filter(t => t.status === 'hold').length,
      completed: list.filter(t => t.status === 'completed').length,
      overdue: list.filter(t => t.due_date && open(t) && dayKey(t.due_date) < today).length,
      due_today: list.filter(t => t.due_date && open(t) && dayKey(t.due_date) === today).length,
    };
  })();
  const toggleGroupCollapsed = (k) => setCollapsedGroups(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => k !== 'date_preset' && (Array.isArray(v) ? v.length : v !== '')).length;

  const handleDelete = (taskId) => {
    showConfirm('Delete Task', 'Delete this task? This cannot be undone.', async () => {
      try {
        const res = await fetch(`${BASE}/api/tasks/${taskId}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          alert(err.message || 'Could not delete this task');
          return;
        }
        handleRefresh();
      } catch (e) { alert('Error deleting task'); }
    });
  };

  const isOverdue = (task) => task.due_date && new Date(task.due_date) < new Date() && !['completed', 'cancelled'].includes(task.status);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  };

  const handleRefresh = () => { fetchTasks(); fetchStats(); fetchTaskGroups(); };

  // No Refresh button: the list reloads after every action, when you come back
  // to this browser tab, and quietly every 2 minutes while the tab is visible
  // (so other people's changes show up). The ref keeps the latest filters.
  const refreshRef = useRef(handleRefresh);
  refreshRef.current = handleRefresh;
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refreshRef.current(); };
    const timer = setInterval(() => { if (document.visibilityState === 'visible') refreshRef.current(); }, 120000);
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  // ── Bulk actions ── each task goes through the same per-task endpoint as a
  // single edit, so every existing rule still applies (role-based status
  // rules, "billed task can't be deleted", history, notifications). Failures
  // are collected per task and reported, never silently skipped.
  const selectedTasks = tasks.filter(t => selectedIds.includes(t.id));
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  // "Select all" covers the rows on screen — not tasks inside a collapsed group.
  const shownTasks = showGroupHeaders ? filtered.filter(t => !collapsedGroups.includes(groupKey(t))) : filtered;
  const allFilteredSelected = shownTasks.length > 0 && shownTasks.every(t => selectedIds.includes(t.id));
  const toggleSelectAll = () => setSelectedIds(allFilteredSelected
    ? selectedIds.filter(id => !shownTasks.some(t => t.id === id))
    : [...new Set([...selectedIds, ...shownTasks.map(t => t.id)])]);

  const runBulk = async (label, list, fn) => {
    setBulkBusy(true); setBulkMsg(null);
    const failures = [];
    const failedIds = [];
    for (const t of list) {
      try {
        const res = await fn(t);
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          failures.push(`${t.task_number}: ${d.message || `failed (${res.status})`}`);
          failedIds.push(t.id);
        }
      } catch (e) { failures.push(`${t.task_number}: network error`); failedIds.push(t.id); }
    }
    setBulkBusy(false);
    const done = list.length - failures.length;
    setBulkMsg({ ok: failures.length === 0, text: `${label}: ${done} of ${list.length} done` + (failures.length ? ` — ${failures.length} failed` : '') });
    if (failures.length) alert(`${label} — these tasks were not changed:\n\n${failures.join('\n')}`);
    setSelectedIds(failedIds); // keep only the ones that didn't go through, so they can be retried
    handleRefresh();
  };

  const putTask = (t, patch) => fetch(`${BASE}/api/tasks/${t.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ ...t, ...patch }),
  });

  const handleBulkStatus = (status) => {
    setBulkStatusOpen(false);
    const list = selectedTasks.filter(t => t.status !== status);
    if (!list.length) { setBulkMsg({ ok: true, text: `All selected tasks are already "${STATUS_LABELS[status]}"` }); return; }
    runBulk(`Status → ${STATUS_LABELS[status]}`, list, t => putTask(t, {
      status, _old_status: t.status,
      completed_date: status === 'completed' ? new Date().toISOString().split('T')[0] : t.completed_date,
    }));
  };

  const handleBulkDelete = () => {
    const n = selectedTasks.length;
    showConfirm('Delete Tasks', `Delete ${n} selected task${n > 1 ? 's' : ''}? This cannot be undone. Billed tasks will be skipped.`, () =>
      runBulk('Delete', selectedTasks, t => fetch(`${BASE}/api/tasks/${t.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })));
  };

  // mode 'add' keeps current assignees and adds these; 'replace' sets exactly these.
  const handleBulkAssign = (userIds, mode) => {
    setBulkAssignOpen(false);
    runBulk(mode === 'add' ? 'Add assignees' : 'Assign users', selectedTasks, t => {
      const current = String(t.assigned_user_ids || t.assigned_to || '').split(',').map(x => parseInt(x)).filter(Boolean);
      const list = mode === 'add' ? [...new Set([...current, ...userIds])] : userIds;
      return putTask(t, { assigned_to: list[0], assigned_users: list });
    });
  };

  // "+ Add New Group" on the strip — creates the group and opens it.
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    const name = newGroupName.trim();
    if (!name || creatingGroup) return;
    setCreatingGroup(true);
    try {
      const res = await fetch(`${BASE}/api/task-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name, color: newGroupColor }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { alert(d.message || 'Could not create the group'); return; }
      setNewGroupOpen(false); setNewGroupName('');
      await fetchTaskGroups();
      // selecting it keeps the new group visible — inline, or highlighted on the "+N more" button
      setGroupFilter(String(d.id));
    } catch (err) { alert('Could not create the group'); }
    finally { setCreatingGroup(false); }
  };

  // Move tasks to a group (groupId null = Ungrouped) — one call for any number of tasks.
  const moveTasksToGroup = async (ids, groupId) => {
    setBulkGroupOpen(false); setGroupMenuTaskId(null); setOpenKebabId(null);
    if (!ids.length) return;
    const bulk = ids.length > 1 || selectedIds.includes(ids[0]);
    if (bulk) { setBulkBusy(true); setBulkMsg(null); }
    try {
      const res = await fetch(`${BASE}/api/tasks/bulk-group`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ task_ids: ids, task_group_id: groupId }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { alert(d.message || 'Could not move the tasks'); return; }
      if (bulk) {
        setBulkMsg({ ok: !d.skipped, text: `Moved ${d.moved} task${d.moved === 1 ? '' : 's'} to ${d.group_name}` + (d.skipped ? ` — ${d.skipped} not allowed` : '') });
        setSelectedIds([]);
      }
      handleRefresh();
    } catch (e) { alert('Could not move the tasks'); }
    finally { if (bulk) setBulkBusy(false); }
  };

  // Per-column cell content for the tasks table — column-driven so the "Customize
  // Columns" gear actually reorders / hides columns.
  const renderTaskCell = (task, key) => {
    const isSubtask = !!task.parent_task_id;
    const overdue = isOverdue(task);
    if (key === 'task_number') return (
      <>
        <button onClick={() => setSelectedTask(task)} style={{ color:'#3b82f6', fontWeight:600, fontSize:12, background:'none', border:'none', cursor:'pointer', padding:0 }}>
          {task.task_number}
        </button>
        {isSubtask && <div style={{ fontSize:11, color:'#818cf8', marginTop:2 }}>↳ subtask</div>}
      </>
    );
    if (key === 'title') return (
      <button onClick={() => setSelectedTask(task)} style={{ textAlign:'left', width:'100%', background:'none', border:'none', cursor:'pointer', padding:0 }}>
        <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
          {task.service_name || task.title || '(No Service)'}
        </p>
        <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{task.client_name || ''}</p>
        {isSubtask && task.parent_task_title && (
          <p style={{ fontSize:11, color:'#818cf8', margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>↳ {task.parent_task_title}</p>
        )}
        {overdue && <span style={{ fontSize:11, color:'#ef4444' }}>⚠ Overdue</span>}
      </button>
    );
    if (key === 'client_name') return (
      <>
        <p style={{ fontSize:12, fontWeight:600, color:T.text2, margin:0, maxWidth:100, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{task.client_name || '-'}</p>
        <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{task.file_number || ''}</p>
      </>
    );
    if (key === 'assigned') {
      const names = (task.assigned_names || task.assigned_name || '').split(',').map(n => n.trim()).filter(Boolean);
      if (names.length === 0) return <span style={{ color:T.text3, fontSize:12 }}>—</span>;
      const BG_COLORS = ['#0f1f45','#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444'];
      return (
        <div style={{ display:'flex', alignItems:'center' }}>
          {names.slice(0,4).map((name, i) => {
            const matchedUser = users.find(u => u.full_name === name);
            return (
              <div key={i} title={name} style={{
                width:26, height:26, borderRadius:'50%', marginLeft: i===0?0:-7,
                background: matchedUser?.avatar_url ? 'transparent' : BG_COLORS[i % BG_COLORS.length],
                border:`2px solid ${T.bg2}`, display:'flex', alignItems:'center', justifyContent:'center',
                color:'#fff', fontSize:10, fontWeight:700, flexShrink:0, position:'relative', zIndex: names.length-i, overflow:'hidden',
              }}>
                {matchedUser?.avatar_url
                  ? <img src={`${BASE}${matchedUser.avatar_url}`} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  : name.charAt(0).toUpperCase()}
              </div>
            );
          })}
          {names.length > 4 && (
            <div style={{ width:26, height:26, borderRadius:'50%', marginLeft:-7, background:T.bg3, border:`2px solid ${T.bg2}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:T.text3, flexShrink:0 }}>
              +{names.length-4}
            </div>
          )}
        </div>
      );
    }
    if (key === 'priority') {
      const PR_META = {
        urgent: { color:'#dc2626', bg: darkMode?'rgba(239,68,68,0.12)':'#fef2f2' },
        high:   { color:'#ea580c', bg: darkMode?'rgba(249,115,22,0.12)':'#fff7ed' },
        medium: { color:'#d97706', bg: darkMode?'rgba(245,158,11,0.12)':'#fffbeb' },
        low:    { color:'#16a34a', bg: darkMode?'rgba(16,185,129,0.12)':'#f0fdf4' },
      };
      const pr = PR_META[task.priority] || { color:T.text3, bg:T.bg3 };
      return <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, textTransform:'capitalize', color:pr.color, background:pr.bg }}>{task.priority}</span>;
    }
    if (key === 'due_date') return <span style={{ color: overdue?'#ef4444':T.text2, fontWeight: overdue?600:400 }}>{formatDate(task.due_date)}</span>;
    if (key === 'target_date') return <span style={{ color:T.text3 }}>{formatDate(task.target_date)}</span>;
    if (key === 'progress') {
      const prog = getProgress(task);
      return (
        <div style={{ width:64 }}>
          <div style={{ width:'100%', background:T.bg3, borderRadius:20, height:6 }}>
            <div style={{ height:6, borderRadius:20, background: prog.pct===100?'#10b981':'#3b82f6', width:`${prog.pct}%`, transition:'width 0.3s' }}/>
          </div>
          <p style={{ fontSize:11, color:T.text3, margin:'3px 0 0' }}>{prog.pct}%</p>
        </div>
      );
    }
    if (key === 'status') {
      const ST_META = {
        pending:     { color:'#d97706', bg: darkMode?'rgba(245,158,11,0.12)':'#fffbeb' },
        in_progress: { color:'#2563eb', bg: darkMode?'rgba(59,130,246,0.12)':'#eff6ff' },
        hold:        { color:'#ea580c', bg: darkMode?'rgba(249,115,22,0.12)':'#fff7ed' },
        completed:   { color:'#16a34a', bg: darkMode?'rgba(16,185,129,0.12)':'#f0fdf4' },
      };
      const sm = ST_META[task.status] || { color:T.text3, bg:T.bg3 };
      return (
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, color:sm.color, background:sm.bg, width:'fit-content' }}>{STATUS_LABELS[task.status]}</span>
          {task.is_billable ? (
            <span style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:600, width:'fit-content',
              color: task.billing_status==='billed'?'#16a34a':'#d97706',
              background: task.billing_status==='billed'?(darkMode?'rgba(16,185,129,0.1)':'#f0fdf4'):(darkMode?'rgba(245,158,11,0.1)':'#fffbeb') }}>
              {task.billing_status==='billed' ? '✓ Billed' : 'Unbilled'}
            </span>
          ) : (
            <span style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:600, width:'fit-content',
              color:T.text3, background: darkMode?'rgba(100,116,139,0.12)':'#f1f5f9' }}>
              Not Billable
            </span>
          )}
        </div>
      );
    }
    if (key === 'tags') return task.tags_data ? (
      <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
        {task.tags_data.split(',').map(t => {
          const [tid, tname, tcolor] = t.split(':');
          return (
            <span key={tid} style={{ padding:'2px 8px', borderRadius:20, fontSize:10.5, fontWeight:600, whiteSpace:'nowrap',
              color: tcolor||'#6366f1', background: (tcolor||'#6366f1') + '1a' }}>
              {tname}
            </span>
          );
        })}
      </div>
    ) : <span style={{ color:T.text3, fontSize:12 }}>—</span>;
    return null;
  };

  return (
    <div style={{
      height:'100vh', padding:24, display:'flex', flexDirection:'column', overflow:'hidden', boxSizing:'border-box',
      background: darkMode
        ? 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.07) 0%,transparent 60%),radial-gradient(ellipse at 20% 100%,rgba(15,31,69,0.4) 0%,transparent 60%),#0a0f1e'
        : 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.05) 0%,transparent 50%),radial-gradient(ellipse at 0% 100%,rgba(15,31,69,0.04) 0%,transparent 50%),#f4f7ff',
    }}>
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title||''}
        message={confirm?.message||''}
        onConfirm={()=>{ confirm?.onConfirm(); setConfirm(null); }}
        onCancel={()=>setConfirm(null)}
      />
      {/* Status tabs — each count is also the filter; actions on the right */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10, padding:'6px 8px 6px 6px', background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12}}>
        <div style={{ flex:1, minWidth:0 }}>
          <PillTabs
            tabs={[
              { key:'all',         label:'All',         count: Number(cardStats.total) || 0 },
              { key:'pending',     label:'Pending',     count: Number(cardStats.pending) || 0 },
              { key:'in_progress', label:'In Progress', count: Number(cardStats.in_progress) || 0 },
              { key:'hold',        label:'On Hold',     count: Number(cardStats.hold) || 0 },
              { key:'overdue',     label:'Overdue',     count: Number(cardStats.overdue) || 0, alert:true },
              { key:'completed',   label:'Completed',   count: Number(cardStats.completed) || 0 },
            ]}
            activeKey={activeTab} onChange={setActiveTab} T={T} darkMode={darkMode} />
        </div>
        <button onClick={exportToXLSX} title="Export the shown tasks to Excel"
          style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12.5, fontWeight:500, cursor:'pointer', flexShrink:0 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='#3b82f6'; e.currentTarget.style.color='#3b82f6'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export
        </button>
        <button onClick={() => setShowAddModal(true)}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12.5, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Task
        </button>
      </div>

      {/* Search + Filter bar */}
      <div style={{ background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}`, padding:10, marginBottom:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <input type="text" placeholder="Search by service, client, task ID..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 14px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }} />
          <select value={filterTaskType} onChange={e => setFilterTaskType(e.target.value)}
            style={{ border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
            <option value="all">All Task Types</option>
            <option value="parent">Parent Tasks Only</option>
            <option value="subtask">Sub-Tasks Only</option>
          </select>
          <button onClick={() => setShowFilters(f => !f)} style={{
            display:'flex', alignItems:'center', gap:7, padding:'8px 14px',
            borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer',
            border: showFilters ? '1.5px solid #3b82f6' : `1.5px solid ${T.border2}`,
            background: showFilters ? (darkMode?'rgba(59,130,246,0.15)':'#eff6ff') : T.bg2,
            color: showFilters ? '#3b82f6' : T.text2,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Filters
            {activeFiltersCount > 0 && <span style={{ background:'#3b82f6', color:'#fff', fontSize:10, borderRadius:'50%', width:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>{activeFiltersCount}</span>}
          </button>
          <p style={{ fontSize:12, color:T.text3, whiteSpace:'nowrap' }}>{filtered.length} found</p>
        </div>

        {showFilters && (
          <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${T.border}`, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            {[
              { label:'Status', field:'status', all:'All Statuses', options: Object.entries(STATUS_LABELS) },
              { label:'Priority', field:'priority', all:'All Priorities', options:[['urgent','Urgent'],['high','High'],['medium','Medium'],['low','Low']] },
              { label:'Assigned To', field:'assigned_to', all:'All Assignees', search:true, options: users.map(u => [String(u.id), u.full_name]) },
              { label:'Client', field:'client_id', all:'All Clients', search:true, options: clients.map(c => [String(c.id), c.client_name + (c.file_number ? ` · ${c.file_number}` : '')]) },
              { label:'Billing Status', field:'billing_status', all:'All Billing', options:[['unbilled','Unbilled'],['billed','Billed'],['non_billable','Not Billable']] },
              { label:'Tags', field:'tags', all:'All Tags', search: allTags.length > 8, options: allTags.map(tg => [String(tg.id), tg.name]) },
            ].map(f => (
              <div key={f.field}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>{f.label}</label>
                <MultiSelectFilter options={f.options} allLabel={f.all} searchable={f.search}
                  selected={filters[f.field]} onChange={vals => setFilters(prev => ({ ...prev, [f.field]: vals }))}
                  darkMode={darkMode} T={T} />
              </div>
            ))}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Due Date Preset</label>
              <select value={filters.date_preset} onChange={e => applyDatePreset(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                <option value="">Select Period</option>
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Due From</label>
              <input type="date" value={filters.date_from}
                onChange={e => setFilters(f => ({ ...f, date_from: e.target.value, date_preset: '' }))}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Due To</label>
              <input type="date" value={filters.date_to}
                onChange={e => setFilters(f => ({ ...f, date_to: e.target.value, date_preset: '' }))}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'flex', alignItems:'flex-end' }}>
              <button onClick={() => setFilters(EMPTY_FILTERS)}
                style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text3, fontSize:12, cursor:'pointer' }}>
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk action bar — appears once one or more tasks are ticked */}
      {(selectedIds.length > 0 || bulkMsg) && (
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:12, padding:'10px 14px', borderRadius:12,
          background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.25)':'#bfdbfe'}` }}>
          {selectedIds.length > 0 && (
            <>
              <span style={{ fontSize:13, fontWeight:700, color:'#1d4ed8' }}>{selectedIds.length} selected</span>
              <div style={{ position:'relative' }}>
                <button type="button" disabled={bulkBusy} onClick={() => { setBulkStatusOpen(o => !o); setBulkGroupOpen(false); }}
                  style={{ padding:'7px 14px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text, fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                  Change Status ▾
                </button>
                {bulkStatusOpen && (
                  <div onMouseLeave={() => setBulkStatusOpen(false)}
                    style={{ position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:90, width:200, padding:5, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:10, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)' }}>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => (
                      <button key={v} type="button" onClick={() => handleBulkStatus(v)}
                        style={{ width:'100%', textAlign:'left', padding:'8px 10px', borderRadius:7, border:'none', background:'transparent', color:T.text, fontSize:12.5, cursor:'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = T.bg3}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>{l}</button>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" disabled={bulkBusy} onClick={() => setBulkAssignOpen(true)}
                style={{ padding:'7px 14px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text, fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                Assign Users
              </button>
              {taskGroups.length > 0 && canMoveGroups && (
                <div style={{ position:'relative' }}>
                  <button type="button" disabled={bulkBusy} onClick={() => { setBulkGroupOpen(o => !o); setBulkStatusOpen(false); }}
                    style={{ padding:'7px 14px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text, fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                    Move to Group ▾
                  </button>
                  {bulkGroupOpen && (
                    <div onMouseLeave={() => setBulkGroupOpen(false)}
                      style={{ position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:90, width:210, maxHeight:300, overflowY:'auto', padding:5, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:10, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)' }}>
                      {[...taskGroups, { id:null, name:'Ungrouped', color:'#94a3b8' }].map(g => (
                        <button key={g.id ?? 'none'} type="button" onClick={() => moveTasksToGroup(selectedIds, g.id)}
                          style={{ width:'100%', display:'flex', alignItems:'center', gap:8, textAlign:'left', padding:'8px 10px', borderRadius:7, border:'none', background:'transparent', color:T.text, fontSize:12.5, cursor:'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = T.bg3}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <span style={{ width:8, height:8, borderRadius:'50%', background:g.color || '#94a3b8', flexShrink:0 }} />{g.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button type="button" disabled={bulkBusy} onClick={handleBulkDelete}
                style={{ padding:'7px 14px', borderRadius:8, border:'1px solid #fecaca', background: darkMode?'rgba(239,68,68,0.12)':'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                Delete
              </button>
              <button type="button" disabled={bulkBusy} onClick={() => setSelectedIds([])}
                style={{ padding:'7px 10px', borderRadius:8, border:'none', background:'none', color:T.text3, fontSize:12, cursor:'pointer' }}>
                Clear selection
              </button>
            </>
          )}
          {bulkBusy && <span style={{ fontSize:12, color:T.text3 }}>Working…</span>}
          {!bulkBusy && bulkMsg && (
            <span style={{ fontSize:12, fontWeight:600, color: bulkMsg.ok ? '#16a34a' : '#ea580c', marginLeft: selectedIds.length ? 'auto' : 0 }}>
              {bulkMsg.text}
              <button type="button" onClick={() => setBulkMsg(null)} style={{ marginLeft:8, border:'none', background:'none', color:T.text3, cursor:'pointer' }}>×</button>
            </span>
          )}
        </div>
      )}
      {bulkAssignOpen && (
        <BulkAssignModal users={users} count={selectedIds.length} onClose={() => setBulkAssignOpen(false)}
          onApply={handleBulkAssign} darkMode={darkMode} T={T} />
      )}

      {/* Tasks Table */}
      <div style={{ flex:1, minHeight:0, background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, display:'flex', flexDirection:'column', overflow:'hidden', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
        {/* Task groups — pills on top of the table; empty groups stay hidden */}
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderBottom:`1px solid ${T.border}` }}>
          <ChipOverflowStrip
            items={[
              { key:'all', name:'All Groups', count: beforeGroup.length },
              // Only groups that have tasks — plus the one being viewed, so a just-created
              // (still empty) group doesn't vanish the moment it opens.
              ...taskGroups
                .map(g => ({ key:String(g.id), name:g.name, color:g.color, count: groupCounts[String(g.id)] || 0 }))
                .filter(g => g.count > 0 || g.key === activeGroup),
              ...(groupCounts.none || activeGroup === 'none' ? [{ key:'none', name:'Ungrouped', color:'#94a3b8', count: groupCounts.none || 0 }] : []),
            ]}
            activeKey={activeGroup} onSelect={setGroupFilter} T={T} darkMode={darkMode} orderKey="tasks-groups" />
          {/* Pinned outside the chip strip so it is always visible */}
          {canManageGroups && <div style={{ position:'relative', flexShrink:0 }}>
            <button type="button" onClick={() => { setNewGroupOpen(o => !o); setNewGroupName(''); }}
              style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:8, whiteSpace:'nowrap', cursor:'pointer',
                fontSize:12, fontWeight:600, border:'1px dashed #3b82f6', background: newGroupOpen ? (darkMode ? 'rgba(59,130,246,0.15)' : '#eff6ff') : 'transparent', color:'#3b82f6' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Group
            </button>
            {newGroupOpen && (
              <form onSubmit={handleCreateGroup}
                style={{ position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:95, width:260, padding:14, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)' }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Group name</label>
                <input autoFocus type="text" maxLength={80} placeholder="e.g. Trust Registration" value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setNewGroupOpen(false); }}
                  style={{ width:'100%', boxSizing:'border-box', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }} />
                <div style={{ display:'flex', gap:6, margin:'10px 0 12px' }}>
                  {GROUP_SWATCHES.map(c => (
                    <button key={c} type="button" onClick={() => setNewGroupColor(c)}
                      style={{ width:18, height:18, borderRadius:'50%', background:c, cursor:'pointer', border: newGroupColor === c ? `2px solid ${T.text}` : '2px solid transparent' }} />
                  ))}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button type="submit" disabled={creatingGroup || !newGroupName.trim()}
                    style={{ flex:1, padding:'8px 12px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12.5, fontWeight:600, cursor:'pointer', opacity: creatingGroup || !newGroupName.trim() ? 0.6 : 1 }}>
                    {creatingGroup ? 'Creating...' : 'Create'}
                  </button>
                  <button type="button" onClick={() => setNewGroupOpen(false)}
                    style={{ padding:'8px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12.5, cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
                <p style={{ fontSize:11, color:T.text3, margin:'10px 0 0' }}>Link service categories to it in Settings → Task Groups.</p>
              </form>
            )}
          </div>}
        </div>
        <div style={{ flex:1, overflowY:'auto', overflowX:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
            <tr>
              <th style={{ padding:'10px 4px 10px 14px', width:28 }}>
                <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll}
                  title="Select all shown tasks" style={{ accentColor:'#3b82f6', cursor:'pointer', width:15, height:15 }} />
              </th>
              {orderedVisibleKeys.map(key => {
                const col = TASK_COLUMNS.find(c => c.key === key);
                const sorted = sortBy.key === key;
                const w = colWidths[key];
                return (
                  <th key={key} onClick={() => toggleSort(key)} title="Click to sort · drag the edge to resize"
                    style={{ position:'relative', padding:'10px 14px 10px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap', cursor:'pointer', userSelect:'none',
                      ...(w ? { width:w, minWidth:w, maxWidth:w, overflow:'hidden', textOverflow:'ellipsis' } : {}) }}>
                    {col?.label}
                    <span style={{ marginLeft:5, fontSize:9, opacity: sorted ? 1 : 0.35 }}>{sorted ? (sortBy.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                    <span onMouseDown={e => startResize(e, key)} onClick={e => e.stopPropagation()} onDoubleClick={e => resetWidth(e, key)}
                      title="Drag to resize · double-click to reset"
                      style={{ position:'absolute', top:0, right:0, width:8, height:'100%', cursor:'col-resize', display:'flex', justifyContent:'center' }}
                      onMouseEnter={e => e.currentTarget.firstChild.style.background = 'rgba(255,255,255,0.7)'}
                      onMouseLeave={e => e.currentTarget.firstChild.style.background = 'rgba(255,255,255,0.18)'}>
                      <span style={{ width:1, height:'60%', marginTop:'auto', marginBottom:'auto', background:'rgba(255,255,255,0.18)' }} />
                    </span>
                  </th>
                );
              })}
              <th style={{ padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>Actions</th>
              <th style={{ padding:'10px 8px', width:40, background:'transparent' }}>
                {cc.menu}
              </th>
            </tr>
          </thead>
          <tbody style={{ minHeight:360 }}>
            {loading ? (
              <tr><td colSpan={visibleColumns.length + 3} style={{ padding:'48px 16px', textAlign:'center', color:T.text3 }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + 3} style={{ padding:'48px 16px', textAlign:'center', color:T.text3 }}>
                {activeGroup !== 'all' && beforeGroup.length > 0
                  ? `No tasks in ${groupInfo(activeGroup).name} yet. Move tasks here from a task's ⋮ menu or the bulk bar, or create a new task.`
                  : 'No tasks found. Click "+ New Task" to create one!'}
              </td></tr>
            ) : filtered.map((task, idx) => {
              const gk = groupKey(task);
              let groupHeader = null;
              if (showGroupHeaders && (idx === 0 || groupKey(filtered[idx - 1]) !== gk)) {
                const gi = groupInfo(gk);
                const collapsed = collapsedGroups.includes(gk);
                const ids = filtered.filter(t => groupKey(t) === gk).map(t => t.id);
                const allOn = ids.every(id => selectedIds.includes(id));
                groupHeader = (
                  <tr key={'g-' + gk} style={{ background: darkMode ? T.bg3 : '#f6f8fc', borderBottom:`1px solid ${T.border}` }}>
                    <td style={{ padding:'8px 4px 8px 14px', width:28 }}>
                      <input type="checkbox" checked={allOn} title={`Select all in ${gi.name}`}
                        onChange={() => setSelectedIds(prev => allOn ? prev.filter(id => !ids.includes(id)) : [...new Set([...prev, ...ids])])}
                        style={{ accentColor:'#3b82f6', cursor:'pointer', width:15, height:15 }} />
                    </td>
                    <td colSpan={visibleColumns.length + 2} onClick={() => toggleGroupCollapsed(gk)}
                      style={{ padding:'8px 12px', cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:8, fontSize:12.5, fontWeight:700, color:T.text }}>
                        <span style={{ display:'inline-block', width:10, color:T.text3, fontSize:10, transform: collapsed ? 'rotate(-90deg)' : 'none', transition:'transform 0.15s' }}>▼</span>
                        <span style={{ width:9, height:9, borderRadius:'50%', background:gi.color || '#94a3b8' }} />
                        {gi.name}
                        <span style={{ fontSize:11, fontWeight:600, color:T.text3 }}>{ids.length}</span>
                      </span>
                    </td>
                  </tr>
                );
              }
              if (showGroupHeaders && collapsedGroups.includes(gk)) return groupHeader;
              const isSubtask = !!task.parent_task_id;
              const overdue = isOverdue(task);
              let rowBg = darkMode ? (idx%2===0?T.bg2:T.bg3) : (idx%2===0?'#fff':'#fafbff');
              if (overdue) rowBg = darkMode ? 'rgba(239,68,68,0.08)' : '#fef2f2';
              else if (isSubtask) rowBg = darkMode ? 'rgba(99,102,241,0.08)' : '#eef2ff';
              return (
                <React.Fragment key={task.id}>
                {groupHeader}
                <tr style={{ borderBottom:`1px solid ${T.border}`, background:rowBg, transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'}
                  onMouseLeave={e => e.currentTarget.style.background = rowBg}>
                  <td style={{ padding:'10px 4px 10px 14px', width:28 }}>
                    <input type="checkbox" checked={selectedIds.includes(task.id)} onChange={() => toggleSelect(task.id)}
                      style={{ accentColor:'#3b82f6', cursor:'pointer', width:15, height:15 }} />
                  </td>
                  {orderedVisibleKeys.map(key => (
                    <td key={key} style={{ padding:'10px 12px', fontSize:12, whiteSpace: key === 'title' ? 'normal' : 'nowrap',
                      ...(colWidths[key]
                        ? { width:colWidths[key], minWidth:colWidths[key], maxWidth:colWidths[key], overflow:'hidden', textOverflow:'ellipsis' }
                        : { maxWidth: key === 'title' ? 240 : key === 'tags' ? 150 : undefined }) }}>
                      {renderTaskCell(task, key)}
                    </td>
                  ))}
                  <td style={{ padding:'10px 12px', position:'relative' }} data-kebab>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        if (openKebabId === task.id) { setOpenKebabId(null); return; }
                        const rect = e.currentTarget.getBoundingClientRect();
                        const spaceBelow = window.innerHeight - rect.bottom;
                        setOpenKebabUp(spaceBelow < 320);
                        setOpenKebabRect(rect);
                        setOpenKebabId(task.id);
                        setStatusChangeId(null);
                      }}
                      style={{ width:32, height:32, borderRadius:8, border:'none', background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                      onMouseLeave={e => e.currentTarget.style.background='none'}
                      data-kebab>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                    </button>
                    {openKebabId === task.id && openKebabRect && (
                      <div
                        style={{
                          position:'fixed',
                          ...(openKebabUp
                            ? { bottom: window.innerHeight - openKebabRect.top + 4 }
                            : { top: openKebabRect.bottom + 4 }),
                          right: window.innerWidth - openKebabRect.right,
                          maxHeight: 'calc(100vh - 24px)', overflowY:'auto',
                          background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:9999, width:208, padding:6 }}
                        data-kebab
                        onMouseLeave={() => { setOpenKebabId(null); setStatusChangeId(null); setAssignTask(null); setGroupMenuTaskId(null); }}>
                        <button onClick={() => { setSelectedTask(task); setOpenKebabId(null); }}
                          style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#3b82f6', fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                          onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.12)':'#eff6ff'}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          View Details
                        </button>
                        <button onClick={() => { setEditTask(task); setOpenKebabId(null); }}
                          style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                          onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          Edit
                        </button>
                        <button onClick={() => { setStatusChangeId(statusChangeId===task.id?null:task.id); }}
                          style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                          onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                          Change Status
                        </button>
                        {statusChangeId === task.id && (
                          <div style={{ background:T.bg3, borderRadius:8, margin:'0 8px 4px', padding:4, display:'flex', flexDirection:'column', gap:2 }}>
                            {Object.entries(STATUS_LABELS).map(([v,l]) => (
                              <button key={v} onClick={() => handleQuickStatus(task.id, v)}
                                style={{ width:'100%', textAlign:'left', padding:'6px 12px', borderRadius:6, fontSize:11, fontWeight:600, border:'none', cursor:'pointer',
                                  background: task.status===v?(darkMode?'rgba(59,130,246,0.15)':'#dbeafe'):'transparent',
                                  color: task.status===v?'#3b82f6':T.text2 }}>
                                {l}
                              </button>
                            ))}
                          </div>
                        )}
                        <button onClick={() => { setAssignTask(task); setOpenKebabId(null); setStatusChangeId(null); }}
                          style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                          onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                          Assign User
                        </button>
                        <button onClick={() => { setTagsTask(task); setOpenKebabId(null); setStatusChangeId(null); }}
                          style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                          onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3H4a1 1 0 0 0-1 1v5.59a2 2 0 0 0 .59 1.41l9.58 9.59a2 2 0 0 0 2.83 0l4.59-4.59a2 2 0 0 0 0-2.83z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>
                          Tags
                        </button>
                        {taskGroups.length > 0 && canMoveGroups && (
                          <button onClick={() => { setGroupMenuTaskId(groupMenuTaskId === task.id ? null : task.id); setStatusChangeId(null); }}
                            style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                            onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                            onMouseLeave={e => e.currentTarget.style.background='none'}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                            Move to Group
                          </button>
                        )}
                        {groupMenuTaskId === task.id && (
                          <div style={{ background:T.bg3, borderRadius:8, margin:'0 8px 4px', padding:4, display:'flex', flexDirection:'column', gap:2 }}>
                            {[...taskGroups, { id:null, name:'Ungrouped', color:'#94a3b8' }].map(g => {
                              const cur = String(task.task_group_id ?? '') === String(g.id ?? '');
                              return (
                                <button key={g.id ?? 'none'} disabled={cur} onClick={() => moveTasksToGroup([task.id], g.id)}
                                  style={{ width:'100%', textAlign:'left', padding:'6px 10px', borderRadius:6, fontSize:11.5, fontWeight:600, border:'none', cursor: cur ? 'default' : 'pointer', display:'flex', alignItems:'center', gap:7,
                                    background: cur ? (darkMode?'rgba(59,130,246,0.15)':'#dbeafe') : 'transparent',
                                    color: cur ? '#3b82f6' : T.text2 }}>
                                  <span style={{ width:7, height:7, borderRadius:'50%', background:g.color || '#94a3b8', flexShrink:0 }} />{g.name}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ height:1, background:T.border, margin:'4px 0' }}/>
                        <button onClick={() => { handleDelete(task.id); setOpenKebabId(null); }}
                          style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#ef4444', fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                          onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                  <td></td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <TaskFormModal token={token} initialClientId={addPrefillClientId}
          onClose={() => { setShowAddModal(false); setAddPrefillClientId(null); }}
          onSuccess={() => { handleRefresh(); setShowAddModal(false); setAddPrefillClientId(null); }}
          darkMode={darkMode} T={T} />
      )}
      {editTask && (
        <TaskFormModal token={token} task={editTask} onClose={() => setEditTask(null)}
          onSuccess={() => { handleRefresh(); setEditTask(null); }}
          darkMode={darkMode} T={T} />
      )}
      {assignTask && (
        <TaskAssignModal
          task={assignTask}
          users={users}
          token={token}
          onClose={() => setAssignTask(null)}
          onSuccess={() => { handleRefresh(); setAssignTask(null); }}
        />
      )}
      {tagsTask && (
        <TaskTagsModal
          task={tagsTask}
          allTags={allTags}
          token={token}
          onClose={() => setTagsTask(null)}
          onSuccess={() => { handleRefresh(); setTagsTask(null); }}
          onTagCreated={tag => setAllTags(prev => [...prev, tag])}
        />
      )}

      {selectedTask && (
        <TaskDetailModal task={selectedTask} token={token} users={users}
          onClose={() => setSelectedTask(null)}
          onSuccess={() => { handleRefresh(); }}
          formatDate={formatDate} showConfirm={showConfirm} darkMode={darkMode} T={T} />
      )}
    </div>
  );
}

// BULK ASSIGN MODAL — pick users, then either add them to each selected task's
// existing team or replace the team outright.
function BulkAssignModal({ users, count, onClose, onApply, darkMode = false, T }) {
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState('add');
  const [q, setQ] = useState('');
  const shown = users.filter(u => !q || u.full_name?.toLowerCase().includes(q.toLowerCase()));
  const toggle = id => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100, padding:16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:400, background:T.bg2, borderRadius:16, boxShadow:'0 24px 60px rgba(15,31,69,0.25)', overflow:'hidden' }}>
        <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.border}` }}>
          <p style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>Assign users</p>
          <p style={{ fontSize:12, color:T.text3, margin:'3px 0 0' }}>{count} task{count > 1 ? 's' : ''} selected</p>
        </div>
        <div style={{ padding:'12px 20px 0', display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
          {[['add','Add to existing team'],['replace','Replace team']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setMode(v)}
              style={{ padding:'8px 0', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer',
                border: mode===v ? 'none' : `1px solid ${T.border2}`, background: mode===v ? '#0f1f45' : T.bg2, color: mode===v ? '#fff' : T.text2 }}>{l}</button>
          ))}
        </div>
        <div style={{ padding:'10px 20px 0' }}>
          <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Search users..."
            style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
        </div>
        <div style={{ maxHeight:280, overflowY:'auto', padding:'8px 14px' }}>
          {shown.map(u => {
            const sel = picked.includes(u.id);
            return (
              <button key={u.id} type="button" onClick={() => toggle(u.id)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'8px', borderRadius:8, border:'none', cursor:'pointer', textAlign:'left', background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent' }}>
                <span style={{ width:16, height:16, borderRadius:4, flexShrink:0, border:`2px solid ${sel?'#2563eb':T.border2}`, background: sel?'#2563eb':'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {sel && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                </span>
                <span style={{ fontSize:13, color:T.text, flex:1 }}>{u.full_name}</span>
                <span style={{ fontSize:11, color:T.text3, textTransform:'capitalize' }}>{u.role}</span>
              </button>
            );
          })}
        </div>
        <div style={{ padding:'12px 20px', borderTop:`1px solid ${T.border}`, display:'flex', gap:10 }}>
          <button type="button" onClick={onClose} style={{ flex:1, padding:'9px 0', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button type="button" disabled={!picked.length} onClick={() => onApply(picked, mode)}
            style={{ flex:2, padding:'9px 0', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor: picked.length?'pointer':'not-allowed', opacity: picked.length?1:0.5 }}>
            {mode === 'add' ? 'Add' : 'Assign'} {picked.length || ''} user{picked.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}

// MULTI-SELECT FILTER — checkbox list with optional search, "Select all"/"Clear".
// Empty selection = no filter (all).
function MultiSelectFilter({ options, selected, onChange, allLabel, searchable = false, darkMode = false, T }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const shown = options.filter(([, l]) => !q || String(l).toLowerCase().includes(q.toLowerCase()));
  const toggle = v => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const label = selected.length === 0 ? allLabel
    : selected.length === 1 ? (options.find(([v]) => v === selected[0])?.[1] || '1 selected')
    : `${selected.length} selected`;
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => { setOpen(o => !o); setQ(''); }}
        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:6, border:`1px solid ${open || selected.length ? '#3b82f6' : T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, background: selected.length ? (darkMode?'rgba(59,130,246,0.1)':'#eff6ff') : T.inputBg, color: selected.length ? '#2563eb' : T.text, cursor:'pointer', textAlign:'left', boxSizing:'border-box' }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight: selected.length ? 600 : 400 }}>{label}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, transform: open?'rotate(180deg)':'none', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, minWidth:200, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:10, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.14)', zIndex:80, overflow:'hidden' }}>
          {searchable && (
            <div style={{ padding:8, borderBottom:`1px solid ${T.border}` }}>
              <input autoFocus type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Search..."
                style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:7, padding:'6px 9px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 10px', borderBottom:`1px solid ${T.border}` }}>
            <button type="button" onClick={() => onChange([...new Set([...selected, ...shown.map(([v]) => v)])])} style={{ fontSize:11, fontWeight:600, color:'#2563eb', background:'none', border:'none', cursor:'pointer', padding:0 }}>Select all</button>
            <button type="button" onClick={() => onChange([])} style={{ fontSize:11, fontWeight:600, color:'#ef4444', background:'none', border:'none', cursor:'pointer', padding:0 }}>Clear</button>
          </div>
          <div style={{ maxHeight:230, overflowY:'auto', padding:4 }}>
            {shown.length === 0 && <p style={{ fontSize:12, color:T.text3, textAlign:'center', padding:'10px 0', margin:0 }}>No options</p>}
            {shown.map(([v, l]) => {
              const sel = selected.includes(v);
              return (
                <button type="button" key={v} onClick={() => toggle(v)}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'7px 8px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12.5, textAlign:'left', color:T.text, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ width:15, height:15, borderRadius:4, flexShrink:0, border:`2px solid ${sel?'#2563eb':T.border2}`, background: sel?'#2563eb':'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {sel && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                  </span>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// TEAM ASSIGN DROPDOWN — multi-select with overlapping avatars, search, and an
// "Assign all" toggle. Same component/visual language as the Clients module's
// UserDropdown, so this looks and behaves identically there and here.
function TeamAssignDropdown({ users, selectedUsers, setSelectedUsers, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const COLORS = ['#0f1f45','#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#ec4899'];

  useEffect(() => {
    const h = e => { if(ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = users.filter(u => u.full_name?.toLowerCase().includes(search.toLowerCase()));
  const selected = users.filter(u => selectedUsers.includes(u.id));

  return (
    <div ref={ref} style={{ position:'relative' }}>
      {/* Trigger */}
      <button type="button" onClick={() => { setOpen(o => !o); setSearch(''); }}
        style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'7px 10px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38, gap:6, boxSizing:'border-box' }}>
        <div style={{ display:'flex', alignItems:'center', gap:4, flex:1, minWidth:0, flexWrap:'nowrap', overflow:'hidden' }}>
          {selected.length === 0 ? (
            <span style={{ color:T.text3, fontSize:12 }}>Select team member...</span>
          ) : (
            <>
              {/* Overlapping avatars */}
              <div style={{ display:'flex', alignItems:'center' }}>
                {selected.slice(0,4).map((u,i) => (
                  <div key={u.id} title={u.full_name} style={{
                    width:22, height:22, borderRadius:'50%', marginLeft: i===0?0:-6,
                    background:`linear-gradient(135deg,${COLORS[i%COLORS.length]},${COLORS[(i+1)%COLORS.length]})`,
                    border:`2px solid ${T.inputBg}`, display:'flex', alignItems:'center',
                    justifyContent:'center', color:'#fff', fontSize:9, fontWeight:700,
                    flexShrink:0, zIndex:4-i, position:'relative',
                  }}>
                    {u.full_name?.charAt(0)?.toUpperCase()}
                  </div>
                ))}
                {selected.length > 4 && (
                  <div style={{ width:22, height:22, borderRadius:'50%', marginLeft:-6, background:T.bg3, border:`2px solid ${T.inputBg}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700, color:T.text3, flexShrink:0 }}>
                    +{selected.length-4}
                  </div>
                )}
              </div>
              <span style={{ fontSize:12, color:T.text2, whiteSpace:'nowrap' }}>
                {selected.length === 1 ? selected[0].full_name : `${selected.length} team members`}
              </span>
            </>
          )}
        </div>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, transform: open?'rotate(180deg)':'rotate(0)', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 4px)', left:0, right:0,
          background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12,
          boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.12)',
          zIndex:60, overflow:'hidden',
        }}>
          {/* Search */}
          <div style={{ padding:'8px 10px', borderBottom:`1px solid ${T.border}` }}>
            <div style={{ position:'relative' }}>
              <svg style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" placeholder="Search team members..." value={search}
                onChange={e => setSearch(e.target.value)} autoFocus
                style={{ width:'100%', paddingLeft:26, paddingRight:10, paddingTop:6, paddingBottom:6, border:`1px solid ${T.border}`, borderRadius:7, fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
            </div>
          </div>

          {/* Assign all toggle */}
          {filtered.length > 1 && (() => {
            const filteredIds = filtered.map(u => u.id);
            const allOn = filteredIds.every(id => selectedUsers.includes(id));
            return (
              <button type="button"
                onClick={() => setSelectedUsers(prev => allOn
                  ? prev.filter(id => !filteredIds.includes(id))
                  : [...new Set([...prev, ...filteredIds])])}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', border:'none', borderBottom:`1px solid ${T.border}`, background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:700, color:'#0f1f45', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${allOn?'#0f1f45':T.border2}`, background: allOn?'#0f1f45':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {allOn && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                {allOn ? 'Unassign all' : `Assign all${search ? ' (matching)' : ''}`}
              </button>
            );
          })()}

          {/* List */}
          <div style={{ maxHeight:200, overflowY:'auto', padding:6, scrollbarWidth:'none' }}>
            {filtered.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:'10px 0', textAlign:'center' }}>No team members found</p>}
            {filtered.map((u, i) => {
              const sel = selectedUsers.includes(u.id);
              return (
                <button key={u.id} type="button"
                  onClick={() => setSelectedUsers(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'7px 8px', borderRadius:8, border:'none', cursor:'pointer', background: sel?(darkMode?'rgba(15,31,69,0.25)':'#f0f4ff'):'transparent', transition:'background 0.1s', textAlign:'left' }}
                  onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                  <div style={{ width:26, height:26, borderRadius:'50%', background:`linear-gradient(135deg,${COLORS[i%COLORS.length]},${COLORS[(i+1)%COLORS.length]})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:10, fontWeight:700, flexShrink:0 }}>
                    {u.full_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:12, fontWeight:600, color:T.text, margin:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.full_name}</p>
                    <p style={{ fontSize:10, color:T.text3, margin:0, textTransform:'capitalize' }}>{u.role}</p>
                  </div>
                  <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${sel?'#0f1f45':T.border2}`, background: sel?'#0f1f45':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.15s' }}>
                    {sel && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          {selectedUsers.length > 0 && (
            <div style={{ padding:'6px 10px', borderTop:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:11, color:T.text3 }}>{selectedUsers.length} selected</span>
              <button type="button" onClick={() => setSelectedUsers([])}
                style={{ fontSize:11, color:'#ef4444', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// EXPENSE CATEGORY DROPDOWN — same look as the New Client "Client Type" picker
// (rounded panel, hover-highlighted rows, inline "+ Add new category" at the
// bottom that turns into a name field + Save). Categories are managed for
// real at Settings → Finance → Expense Categories; this can only add one.
function CategoryDropdown({ categories, value, onChange, onCreate, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.name) onChange(created.name);
      setNewName(''); setAdding(false); setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
          padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center',
          justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38,
          transition:'border-color 0.15s', boxSizing:'border-box',
        }}>
        <span style={{ color: value ? T.text : T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {value || 'Select category...'}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:6, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:60, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div style={{ maxHeight:210, overflowY:'auto', padding:5 }}>
            {categories.map(c => {
              const name = c.name || c;
              const sel = name === value;
              return (
                <button key={c.id||name} type="button" onClick={() => { onChange(name); setOpen(false); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {categories.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>No categories configured</p>}
          </div>

          <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
            {adding ? (
              <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                <input autoFocus type="text" value={newName} placeholder="e.g. Courier, Stationery"
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
                  style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                  {busy ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add New Category
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Default modes shown until the firm configures its own list at
// Settings → Billing & Finance → Payment Modes.
const FALLBACK_PAYMENT_MODES = ['cash','bank_transfer','upi','cheque','card','online','other']
  .map(v => ({ value: v, name: v.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase()) }));

// PAYMENT MODE DROPDOWN — same shell as CategoryDropdown (rounded panel,
// checkmark on the selected row, inline "+ Add New Mode" at the bottom) so
// Payment Mode and Category read as one consistent picker style, not a plain
// OS-styled <select> next to a custom one.
// Bank Account is the mandatory "where did the money actually move" field on
// a Task Expense — independent of Payment Mode (optional, purely descriptive:
// NEFT/UPI/Cheque/Card). "Cash" is the default and maps to no bank account.
function BankAccountSelect({ accounts, value, onChange, darkMode = false, T: theme }) {
  const T = theme || { inputBg:'#fff', inputBorder:'#e2e8f0', text:'#0f1f45' };
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={is}>
      <option value="">Cash</option>
      {accounts.map(a => (
        <option key={a.id} value={a.id}>{a.bank_name}{a.nickname ? ` — ${a.nickname}` : ''}{a.account_last4 ? ` (••${a.account_last4})` : ''}</option>
      ))}
    </select>
  );
}

function PaymentModeDropdown({ modes, value, onChange, onCreate, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const list = modes && modes.length ? modes : FALLBACK_PAYMENT_MODES;
  const selected = list.find(m => m.value === value);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.value) onChange(created.value);
      setNewName(''); setAdding(false); setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
          padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center',
          justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38,
          transition:'border-color 0.15s', boxSizing:'border-box',
        }}>
        <span style={{ color: selected ? T.text : T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {selected?.name || 'Select mode...'}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:6, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:60, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div style={{ maxHeight:210, overflowY:'auto', padding:5 }}>
            {list.map(m => {
              const sel = m.value === value;
              return (
                <button key={m.id||m.value} type="button" onClick={() => { onChange(m.value); setOpen(false); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{m.name}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
          </div>

          <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
            {adding ? (
              <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                <input autoFocus type="text" value={newName} placeholder="e.g. Wallet, NEFT"
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
                  style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                  {busy ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add New Mode
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TASK FORM MODAL ──────────────────────────────────────────────────────────
function TaskFormModal({ token, task, initialClientId, onClose, onSuccess, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: task?.title || '',
    client_id: task?.client_id || '',
    service_id: task?.service_id || '',
    assigned_to: task?.assigned_to || '',
    assigned_users: task?.assigned_user_ids
      ? task.assigned_user_ids.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
      : (task?.assigned_to ? [Number(task.assigned_to)] : []),
    status: task?.status || 'pending',
    priority: task?.priority || 'medium',
    due_date: task?.due_date ? task.due_date.split('T')[0] : '',
    target_date: task?.target_date ? task.target_date.split('T')[0] : '', // Point 7
    estimated_hours: task?.estimated_hours || '',
    is_billable: task?.is_billable !== false,
    // Self-heals older rows saved before Is Billable / Billing Status were kept
    // in lockstep — not billable always normalizes to 'non_billable' here too.
    billing_status: task?.is_billable === false ? 'non_billable' : (task?.billing_status && task.billing_status !== 'non_billable' ? task.billing_status : 'unbilled'),
    notes: task?.notes || '',
    parent_task_id: task?.parent_task_id || '',
    // '' = automatic (from the service / parent task), 'none' = Ungrouped, else a group id
    task_group_id: task ? (task.task_group_id ? String(task.task_group_id) : 'none') : '',
    referral_commission_type: task?.referral_commission_type || '',
    referral_commission_value: task?.referral_commission_value ?? '',
    referral_commission_notes: task?.referral_commission_notes || '',
  });
  const [clientName, setClientName] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [allTasks, setAllTasks] = useState([]);
  const [parentSearch, setParentSearch] = useState('');
  const [showParentPicker, setShowParentPicker] = useState(false);
  const [taskGroups, setTaskGroups] = useState([]);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const groupTouched = useRef(false); // once the user picks a group, a service change no longer overrides it

  // Each of these loads independently — Client/Service/Team are required to even
  // fill in the form, so they must never be gated behind the (rarely used, and
  // much heavier — every row carries checklist/subtask/tag/team subqueries)
  // Parent Task list. Previously all four were behind one Promise.all, so if
  // /api/tasks was slow (or errored) on a firm with many tasks, the whole
  // form — including the Client picker — sat empty and unusable.
  const authHeaders = { 'Authorization': `Bearer ${token}` };
  useEffect(() => {
    fetch(`${BASE}/api/clients`, { headers: authHeaders }).then(r => r.json())
      .then(c => setClients(Array.isArray(c) ? c : [])).catch(console.error);
    fetch(`${BASE}/api/services`, { headers: authHeaders }).then(r => r.json())
      .then(s => setServices(Array.isArray(s) ? s : [])).catch(console.error);
    fetch(`${BASE}/api/users`, { headers: authHeaders }).then(r => r.json())
      .then(u => setUsers(Array.isArray(u) ? u : [])).catch(console.error);
    fetch(`${BASE}/api/task-groups`, { headers: authHeaders }).then(r => r.json())
      .then(g => setTaskGroups(Array.isArray(g) ? g : [])).catch(console.error);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parent Task candidates are fetched lazily — only the first time that picker
  // is actually opened — since most tasks are never made into a sub-task.
  // parentTasksRequested dedupes the fetch; parentTasksLoaded (true only once
  // the response lands) drives the "Loading tasks..." vs "No tasks found" label.
  const [parentTasksRequested, setParentTasksRequested] = useState(false);
  const [parentTasksLoaded, setParentTasksLoaded] = useState(false);
  const loadParentTaskCandidates = () => {
    if (parentTasksRequested) return;
    setParentTasksRequested(true);
    fetch(`${BASE}/api/tasks`, { headers: authHeaders }).then(r => r.json())
      .then(t => { setAllTasks(Array.isArray(t) ? t : []); setParentTasksLoaded(true); })
      .catch(e => { console.error(e); setParentTasksLoaded(true); });
  };

  // Point 6: Auto-generate title when client + service both selected
  const handleClientChange = (clientId) => {
    const client = clients.find(c => String(c.id) === String(clientId));
    const cName = client?.client_name || '';
    setClientName(cName);
    setForm(f => {
      const autoTitle = serviceName && cName ? `${serviceName} — ${cName}` : f.title;
      return { ...f, client_id: clientId, title: autoTitle };
    });
  };

  // Pre-select the client when opened from a Client Dashboard — runs once the
  // client list has loaded so the name (and auto-title) resolve too.
  const prefillDone = useRef(false);
  useEffect(() => {
    if (prefillDone.current || !initialClientId || !clients.length) return;
    prefillDone.current = true;
    handleClientChange(initialClientId);
  }, [clients]); // eslint-disable-line react-hooks/exhaustive-deps

  // The group a service's category feeds (Settings → Services & Tasks → Task Groups).
  const groupForService = (service) => {
    const cat = String(service?.category || '').trim().toLowerCase();
    return cat ? taskGroups.find(g => (g.service_categories || []).some(c => String(c).trim().toLowerCase() === cat)) : null;
  };

  const handleServiceChange = (serviceId) => {
    const service = services.find(s => String(s.id) === String(serviceId));
    const autoGroup = groupForService(service);
    const sName = service?.name || '';
    setServiceName(sName);
    setForm(f => {
      const autoTitle = sName && clientName ? `${sName} — ${clientName}` : f.title;
      const next = { ...f, service_id: serviceId, title: autoTitle };
      if (!groupTouched.current && autoGroup) next.task_group_id = String(autoGroup.id);
      if (service) {
        // A service whose "Mark tasks billable by default" is off should always produce
        // non-billable tasks, even created manually here — not just via the recurring generator.
        next.is_billable = service.is_billable_by_default !== false;
        // Prefill the assignee from the service's "Default Users (Assign to)" when the form
        // doesn't already have one chosen.
        if (!f.assigned_to && Array.isArray(service.default_assign_user_ids) && service.default_assign_user_ids.length > 0) {
          next.assigned_to = String(service.default_assign_user_ids[0]);
        }
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const url = task ? `${BASE}/api/tasks/${task.id}` : `${BASE}/api/tasks`;
      const method = task ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify((({ task_group_id, ...rest }) => ({
          ...rest, assigned_to: form.assigned_users[0] || form.assigned_to || '',
          ...(task_group_id ? { task_group_id } : {}), // '' → server picks it from the service / parent
        }))(form))
      });
      const data = await res.json();
      if (res.ok) onSuccess();
      else alert(data.message || 'Error saving task');
    } catch (e) { alert('Server error'); }
    finally { setSaving(false); }
  };

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>{children}</label>;
  const SC = ({ title }) => <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:14 }}>{title}</p>;

  const autoTitle = (() => {
    const s = services.find(x => String(x.id) === String(form.service_id));
    const c = clients.find(x => String(x.id) === String(form.client_id));
    return s && c ? `${s.name} — ${c.client_name}` : '';
  })();

  const selectedClientObj = clients.find(c => String(c.id) === String(form.client_id));
  const selectedClientName = selectedClientObj?.client_name || '';
  // Same-named clients are common, so every row carries identifiers (file no. ·
  // group · mobile · PAN) and the search matches on them too.
  const clientMeta = (c) => [c.file_number, c.group_name, c.mobile, c.pan].filter(Boolean);
  const filteredClients = clients.filter(c => {
    if (!clientSearch) return true;
    const q = clientSearch.toLowerCase();
    return c.client_name?.toLowerCase().includes(q) || clientMeta(c).some(m => String(m).toLowerCase().includes(q));
  });

  const serviceLabel = (s) => `${s.service_code ? `[${s.service_code}] ` : ''}${s.name}`;
  const selectedServiceLabel = (() => {
    const s = services.find(x => String(x.id) === String(form.service_id));
    return s ? serviceLabel(s) : '';
  })();
  const filteredServices = services.filter(s => !serviceSearch ||
    s.name?.toLowerCase().includes(serviceSearch.toLowerCase()) ||
    s.service_code?.toLowerCase().includes(serviceSearch.toLowerCase()));

  const parentCandidates = allTasks.filter(t => !task || t.id !== task.id);
  const selectedParentTask = parentCandidates.find(t => String(t.id) === String(form.parent_task_id));
  const selectedParentLabel = selectedParentTask ? `${selectedParentTask.task_number} — ${selectedParentTask.title}` : '';
  const filteredParentTasks = parentCandidates.filter(t => !parentSearch ||
    t.title?.toLowerCase().includes(parentSearch.toLowerCase()) ||
    t.task_number?.toLowerCase().includes(parentSearch.toLowerCase()));

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:12 }}>
      <div style={{ background:T.bg2, borderRadius:20, display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)', width:'100%', maxWidth:1440, height:'94vh' }}>

        {/* HEADER */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 24px', borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.bg2, borderRadius:'20px 20px 0 0' }}>
          <div>
            <h2 style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>
              {task ? `Edit Task — ${task.task_number}` : 'New Task'}
            </h2>
            <p style={{ fontSize:14, color:T.text3, marginTop:3 }}>
              {task ? 'Update the task details below' : 'Fill in the details below to create a new task'}
            </p>
          </div>
          <button type="button" onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', border:'none', background:T.bg3, color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit} style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column' }}>
          <div style={{ flex:1, minHeight:0, display:'flex', overflow:'hidden' }}>

            {/* COL 1 — 50% */}
            <div style={{ width:'50%', borderRight:`1px solid ${T.border}`, flexShrink:0, display:'flex', flexDirection:'column', gap:18, background:T.bg3, padding:24, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
              <div>
                <SC title="Task Identity" />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18 }}>
                  <div style={{ position:'relative' }}>
                    <SL>Client</SL>
                    <button type="button" onClick={() => { setClientSearch(''); setShowClientPicker(p => !p); }}
                      style={{ ...is, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
                      <span style={{ color: selectedClientName ? T.text : T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {selectedClientName || 'Select client...'}
                        {selectedClientName && selectedClientObj?.file_number && <span style={{ color:T.text3, fontSize:11, marginLeft:8 }}>{selectedClientObj.file_number}</span>}
                      </span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, marginLeft:6, transform: showClientPicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {showClientPicker && (
                      <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, width:'100%' }}
                        onMouseLeave={() => setShowClientPicker(false)}>
                        <div style={{ padding:10, borderBottom:`1px solid ${T.border}` }}>
                          <div style={{ position:'relative' }}>
                            <svg style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            <input type="text" autoFocus placeholder="Search clients..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                              style={{ width:'100%', paddingLeft:28, paddingRight:10, paddingTop:8, paddingBottom:8, border:`1px solid ${T.inputBorder}`, borderRadius:8, fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                          </div>
                        </div>
                        <div style={{ maxHeight:220, overflowY:'auto' }}>
                          {filteredClients.length === 0 ? (
                            <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>No clients found</p>
                          ) : filteredClients.map(c => {
                            const sel = String(form.client_id) === String(c.id);
                            return (
                              <button key={c.id} type="button" onClick={() => { handleClientChange(c.id); setShowClientPicker(false); }}
                                style={{ width:'100%', textAlign:'left', padding:'9px 14px', border:'none', cursor:'pointer', fontSize:13, fontWeight:500, background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent', color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text }}
                                onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                                onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                                <span style={{ display:'block' }}>{c.client_name}</span>
                                {clientMeta(c).length > 0 && (
                                  <span style={{ display:'block', marginTop:2, fontSize:11, fontWeight:400, color:T.text3 }}>
                                    {clientMeta(c).join(' · ')}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ position:'relative' }}>
                    <SL>Service</SL>
                    <button type="button" onClick={() => { setServiceSearch(''); setShowServicePicker(p => !p); }}
                      style={{ ...is, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
                      <span style={{ color: selectedServiceLabel ? T.text : T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedServiceLabel || 'Select service...'}</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, marginLeft:6, transform: showServicePicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {showServicePicker && (
                      <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, width:'100%' }}
                        onMouseLeave={() => setShowServicePicker(false)}>
                        <div style={{ padding:10, borderBottom:`1px solid ${T.border}` }}>
                          <div style={{ position:'relative' }}>
                            <svg style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            <input type="text" autoFocus placeholder="Search services..." value={serviceSearch} onChange={e => setServiceSearch(e.target.value)}
                              style={{ width:'100%', paddingLeft:28, paddingRight:10, paddingTop:8, paddingBottom:8, border:`1px solid ${T.inputBorder}`, borderRadius:8, fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                          </div>
                        </div>
                        <div style={{ maxHeight:220, overflowY:'auto' }}>
                          {filteredServices.length === 0 ? (
                            <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>No services found</p>
                          ) : filteredServices.map(s => {
                            const sel = String(form.service_id) === String(s.id);
                            return (
                              <button key={s.id} type="button" onClick={() => { handleServiceChange(s.id); setShowServicePicker(false); }}
                                style={{ width:'100%', textAlign:'left', padding:'9px 14px', border:'none', cursor:'pointer', fontSize:13, fontWeight:500, background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent', color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text }}
                                onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                                onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                                {serviceLabel(s)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Task Group — filled from the service; can be changed */}
              {taskGroups.length > 0 && (() => {
                const autoGroup = groupForService(services.find(s => String(s.id) === String(form.service_id)));
                const cur = form.task_group_id === 'none' ? { name:'Ungrouped', color:'#94a3b8' }
                  : taskGroups.find(g => String(g.id) === form.task_group_id) || null;
                const isAuto = !groupTouched.current && cur && autoGroup && String(autoGroup.id) === form.task_group_id;
                const pick = (v) => { groupTouched.current = true; setForm(f => ({ ...f, task_group_id: v })); setShowGroupPicker(false); };
                return (
                  <div style={{ position:'relative' }}>
                    <SL>Task Group</SL>
                    <button type="button" onClick={() => setShowGroupPicker(p => !p)}
                      style={{ ...is, textAlign:'left', display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                      {cur && <span style={{ width:9, height:9, borderRadius:'50%', background:cur.color || '#94a3b8', flexShrink:0 }} />}
                      <span style={{ color: cur ? T.text : T.text3, flex:1 }}>{cur ? cur.name : 'Automatic — from the service'}</span>
                      {isAuto && <span style={{ fontSize:11, color:'#16a34a', fontWeight:500 }}>from service</span>}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, transform: showGroupPicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {showGroupPicker && (
                      <div onMouseLeave={() => setShowGroupPicker(false)}
                        style={{ position:'absolute', left:0, top:'calc(100% + 4px)', width:'100%', zIndex:60, maxHeight:240, overflowY:'auto', padding:5, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)' }}>
                        {[...taskGroups.map(g => ({ v:String(g.id), name:g.name, color:g.color })), { v:'none', name:'Ungrouped', color:'#94a3b8' }].map(o => {
                          const sel = form.task_group_id === o.v;
                          return (
                            <button key={o.v} type="button" onClick={() => pick(o.v)}
                              style={{ width:'100%', display:'flex', alignItems:'center', gap:8, textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:500,
                                background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent', color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text }}>
                              <span style={{ width:8, height:8, borderRadius:'50%', background:o.color || '#94a3b8', flexShrink:0 }} />
                              {o.name}
                              {autoGroup && String(autoGroup.id) === o.v && <span style={{ marginLeft:'auto', fontSize:11, color:T.text3, fontWeight:400 }}>service's group</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Parent Task (optional) — links this task as a Sub-Task under another */}
              <div style={{ position:'relative' }}>
                <SL>Parent Task <span style={{ textTransform:'none', fontWeight:400, letterSpacing:0 }}>(optional — makes this a Sub-Task)</span></SL>
                <button type="button" onClick={() => { setParentSearch(''); setShowParentPicker(p => !p); loadParentTaskCandidates(); }}
                  style={{ ...is, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
                  <span style={{ color: selectedParentLabel ? T.text : T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedParentLabel || 'None — this is a standalone task'}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, marginLeft:6, transform: showParentPicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {showParentPicker && (
                  <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, width:'100%' }}
                    onMouseLeave={() => setShowParentPicker(false)}>
                    <div style={{ padding:10, borderBottom:`1px solid ${T.border}` }}>
                      <div style={{ position:'relative' }}>
                        <svg style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input type="text" autoFocus placeholder="Search tasks by title or number..." value={parentSearch} onChange={e => setParentSearch(e.target.value)}
                          style={{ width:'100%', paddingLeft:28, paddingRight:10, paddingTop:8, paddingBottom:8, border:`1px solid ${T.inputBorder}`, borderRadius:8, fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                      </div>
                    </div>
                    <div style={{ maxHeight:220, overflowY:'auto' }}>
                      <button type="button" onClick={() => { setForm(f => ({ ...f, parent_task_id: '' })); setShowParentPicker(false); }}
                        style={{ width:'100%', textAlign:'left', padding:'9px 14px', border:'none', cursor:'pointer', fontSize:13, fontWeight:500, background: !form.parent_task_id?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent', color: !form.parent_task_id?(darkMode?'#93c5fd':'#2563eb'):T.text3 }}
                        onMouseEnter={e => { if(form.parent_task_id) e.currentTarget.style.background=T.bg3; }}
                        onMouseLeave={e => { if(form.parent_task_id) e.currentTarget.style.background='transparent'; }}>
                        None — standalone task
                      </button>
                      {filteredParentTasks.length === 0 ? (
                        <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>{parentTasksLoaded ? 'No tasks found' : 'Loading tasks...'}</p>
                      ) : filteredParentTasks.map(t => {
                        const sel = String(form.parent_task_id) === String(t.id);
                        return (
                          <button key={t.id} type="button" onClick={() => { setForm(f => ({ ...f, parent_task_id: t.id })); setShowParentPicker(false); }}
                            style={{ width:'100%', textAlign:'left', padding:'9px 14px', border:'none', cursor:'pointer', fontSize:13, fontWeight:500, background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent', color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text }}
                            onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                            onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                            <span style={{ fontFamily:'monospace', fontSize:11, color:'#3b82f6', marginRight:8 }}>{t.task_number}</span>
                            {t.title}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Auto-title preview */}
              {autoTitle && (
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:10, background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}` }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  <p style={{ fontSize:13, color: darkMode?'#93c5fd':'#1d4ed8', margin:0 }}>Task title: <strong>{autoTitle}</strong></p>
                </div>
              )}

              <div>
                <SL>Task Title {!autoTitle && <span style={{ color:'#ef4444' }}>*</span>}</SL>
                <input required={!autoTitle} type="text"
                  placeholder={autoTitle ? 'Auto-generated from Service + Client' : 'Enter task title...'}
                  value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={is} />
              </div>

              <div>
                <SC title="Team & Priority" />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18 }}>
                  <div>
                    <SL>Assign To</SL>
                    <TeamAssignDropdown users={users}
                      selectedUsers={form.assigned_users}
                      setSelectedUsers={updater => setForm(f => ({ ...f, assigned_users: typeof updater==='function' ? updater(f.assigned_users) : updater }))}
                      darkMode={darkMode} T={T} />
                  </div>
                  <div>
                    <SL>Status</SL>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={is}>
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Priority chips */}
              <div>
                <SL>Priority</SL>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
                  {[
                    { v:'low',    l:'Low',    bg:'#64748b' },
                    { v:'medium', l:'Medium', bg:'#3b82f6' },
                    { v:'high',   l:'High',   bg:'#f97316' },
                    { v:'urgent', l:'Urgent', bg:'#ef4444' },
                  ].map(({ v, l, bg }) => (
                    <button key={v} type="button" onClick={() => setForm(f => ({ ...f, priority: v }))} style={{
                      padding:'10px 0', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer',
                      border: form.priority===v ? 'none' : `1.5px solid ${T.border2}`,
                      background: form.priority===v ? bg : T.bg2,
                      color: form.priority===v ? '#fff' : T.text2,
                    }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Auto-features info */}
              {!task && (
                <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, padding:16 }}>
                  <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Auto-features on Create</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {[
                      'Checklist auto-copied from Services Master',
                      'Subtasks auto-assigned to selected team member',
                      'Task title auto-generated from Service + Client',
                    ].map((t,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        <span style={{ fontSize:13, color:T.text2 }}>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* COL 2 — 25%: Dates */}
            <div style={{ width:'25%', borderRight:`1px solid ${T.border}`, flexShrink:0, display:'flex', flexDirection:'column', gap:18, background:T.bg2, padding:24, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
              <SC title="Dates & Deadlines" />
              <SmartDatePicker label="Due Date" value={form.due_date} onChange={v => setForm(f => ({ ...f, due_date: v }))} darkMode={darkMode} T={T} />
              <SmartDatePicker label="Target Date (Internal)" value={form.target_date} onChange={v => setForm(f => ({ ...f, target_date: v }))} darkMode={darkMode} T={T} />
              <div>
                <SL>Estimated Hours</SL>
                <input type="number" step="0.5" min="0" placeholder="e.g. 2.5"
                  value={form.estimated_hours} onChange={e => setForm(f => ({ ...f, estimated_hours: e.target.value }))} style={is} />
                <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>Used to track time utilization</p>
              </div>
            </div>

            {/* COL 3 — 25%: Billing + Notes */}
            <div style={{ width:'25%', flexShrink:0, display:'flex', flexDirection:'column', gap:18, background:T.bg3, padding:24, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
              <div>
                <SC title="Billing" />
                <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, overflow:'hidden' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px' }}>
                    <div>
                      <p style={{ fontSize:13, fontWeight:500, color:T.text, margin:0 }}>Is Billable</p>
                      <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>Include in invoicing</p>
                    </div>
                    <button type="button" onClick={() => setForm(f => {
                      const nowBillable = !f.is_billable;
                      // Keep billing_status in lockstep: OFF always means "Not Billable" (and
                      // therefore can never get invoiced — see the Create Invoice gate below,
                      // which already requires is_billable AND billing_status==='unbilled').
                      // Switching back ON drops out of that locked state into the normal flow.
                      return {
                        ...f, is_billable: nowBillable,
                        billing_status: nowBillable ? (f.billing_status==='non_billable' ? 'unbilled' : f.billing_status) : 'non_billable',
                      };
                    })}
                      style={{ position:'relative', width:38, height:20, borderRadius:10, border:'none', cursor:'pointer', background: form.is_billable?'#3b82f6':(darkMode?'#334155':'#d1d5db'), transition:'background 0.2s', flexShrink:0 }}>
                      <span style={{ position:'absolute', top:2, left: form.is_billable?20:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
                    </button>
                  </div>
                </div>

                <div style={{ marginTop:16 }}>
                  <SL>Billing Status</SL>
                  {!form.is_billable ? (
                    <div style={{ padding:'10px 0', borderRadius:10, textAlign:'center', fontSize:12, fontWeight:600, background:T.bg3, color:T.text3, border:`1.5px solid ${T.border2}` }}>
                      Not Billable
                    </div>
                  ) : (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
                      {[['unbilled','Unbilled'],['billed','Billed']].map(([v,l]) => (
                        <button key={v} type="button" onClick={() => setForm(f => ({ ...f, billing_status: v }))} style={{
                          padding:'10px 0', borderRadius:10, fontSize:12, fontWeight:600, cursor:'pointer',
                          border: form.billing_status===v ? 'none' : `1.5px solid ${T.border2}`,
                          background: form.billing_status===v ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg2,
                          color: form.billing_status===v ? '#fff' : T.text2,
                        }}>
                          {l}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Referral Commission — task-level override of the service /
                    partner default. Blank = inherit. */}
                <div style={{ marginTop:16 }}>
                  <SL>Referral Commission (this task)</SL>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <select value={form.referral_commission_type}
                      onChange={e => setForm(f => ({ ...f, referral_commission_type: e.target.value }))} style={is}>
                      <option value="">Inherit (service/partner)</option>
                      <option value="percentage">Percentage %</option>
                      <option value="fixed">Fixed ₹</option>
                      <option value="none">No commission</option>
                    </select>
                    <input type="number" min="0" step="0.01"
                      disabled={!form.referral_commission_type || form.referral_commission_type === 'none'}
                      placeholder={form.referral_commission_type === 'percentage' ? '% rate' : '₹ amount'}
                      value={form.referral_commission_value}
                      onChange={e => setForm(f => ({ ...f, referral_commission_value: e.target.value }))} style={is} />
                  </div>
                  <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>
                    Overrides the service &amp; partner commission for referrals tied to this task.
                  </p>
                </div>
              </div>

              <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:18 }}>
                <SL>Notes</SL>
                <textarea rows="7" placeholder="Task notes, instructions, or any remarks for the team..."
                  value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  style={{ ...is, resize:'none', lineHeight:1.6, padding:'10px 14px' }} />
              </div>
            </div>

          </div>

          {/* FOOTER */}
          <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:10, flexShrink:0, background:T.bg2, borderRadius:'0 0 20px 20px' }}>
            <button type="button" onClick={onClose} style={{ padding:'10px 24px', borderRadius:10, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={{
              marginLeft:'auto', padding:'10px 36px', borderRadius:10, border:'none',
              background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff',
              fontSize:13, fontWeight:700, cursor:'pointer',
              boxShadow:'0 4px 12px rgba(15,31,69,0.3)', opacity: saving?0.7:1,
              display:'flex', alignItems:'center', gap:8,
            }}>
              {saving && <span style={{ width:13, height:13, border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'#fff', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
              {saving ? 'Saving...' : task ? 'Save Changes' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── TASK DETAIL MODAL ────────────────────────────────────────────────────────
function SubtaskRow({ subtask, index, users, onStatusChange, onAssignUser, STATUS_COLORS, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [openKebab, setOpenKebab] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpenKebab(false); setShowAssign(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div style={{ background: subtask.status==='completed'?(darkMode?'rgba(16,185,129,0.06)':T.bg2):T.bg2, border:`1px solid ${subtask.status==='completed'?(darkMode?'rgba(16,185,129,0.2)':'#bbf7d0'):T.border}`, borderRadius:12, padding:16 }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:12, flex:1 }}>
          <span style={{ fontSize:11, fontWeight:700, color:T.text3, marginTop:2, width:16, flexShrink:0 }}>{index+1}</span>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ fontSize:13, fontWeight:600, color: subtask.status==='completed'?'#16a34a':T.text, margin:0 }}>{subtask.subtask_name}</p>
            {subtask.description && <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>{subtask.description}</p>}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginTop:6, flexWrap:'wrap' }}>
              {subtask.assigned_name ? (
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ width:16, height:16, borderRadius:'50%', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:8, fontWeight:700 }}>
                    {subtask.assigned_name.charAt(0).toUpperCase()}
                  </div>
                  <span style={{ fontSize:11, color:T.text2 }}>{subtask.assigned_name}</span>
                </div>
              ) : (
                <span style={{ fontSize:11, color:T.text3, fontStyle:'italic' }}>Auto-assigned from parent</span>
              )}
              {subtask.due_date && <span style={{ fontSize:11, color:T.text3 }}>Due: {new Date(subtask.due_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>}
              {subtask.target_date && <span style={{ fontSize:11, color:'#f97316' }}>Target: {new Date(subtask.target_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>}
            </div>
          </div>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          <select value={subtask.status} onChange={e => onStatusChange(subtask, e.target.value)}
            style={{ fontSize:11, border:`1px solid ${T.border2}`, borderRadius:8, padding:'5px 8px', outline:'none', background:T.inputBg, color:T.text, cursor:'pointer' }}>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>

          <div style={{ position:'relative' }} ref={ref}>
            <button type="button" onClick={() => setOpenKebab(o => !o)}
              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', display:'flex', alignItems:'center', justifyContent:'center', color:T.text3, cursor:'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg3}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
            </button>
            {openKebab && (
              <div style={{ position:'absolute', right:0, top:32, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 8px 32px rgba(0,0,0,0.4)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:999, width:176, padding:6 }}>
                <button type="button" onClick={() => { setShowAssign(s => !s); }}
                  style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                  onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                  onMouseLeave={e => e.currentTarget.style.background='none'}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  Assign User
                </button>
              </div>
            )}
            {showAssign && (
              <div style={{ position:'absolute', right:0, top:32, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 8px 32px rgba(0,0,0,0.4)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:9999, width:208, padding:8 }}>
                <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8, padding:'0 4px' }}>Assign to</p>
                <div style={{ display:'flex', flexDirection:'column', gap:2, maxHeight:176, overflowY:'auto', scrollbarWidth:'none' }}>
                  {users.map(u => {
                    const sel = String(subtask.assigned_to) === String(u.id);
                    return (
                      <button key={u.id} type="button"
                        onClick={() => { onAssignUser(subtask, u.id); setShowAssign(false); setOpenKebab(false); }}
                        style={{ width:'100%', textAlign:'left', padding:'7px 10px', borderRadius:8, border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:10, background: sel?(darkMode?'rgba(59,130,246,0.12)':'#eff6ff'):'transparent' }}
                        onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                        onMouseLeave={e => { if(!sel) e.currentTarget.style.background=sel?(darkMode?'rgba(59,130,246,0.12)':'#eff6ff'):'transparent'; }}>
                        <div style={{ width:24, height:24, borderRadius:'50%', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:10, fontWeight:700, flexShrink:0 }}>
                          {u.full_name.charAt(0)}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <p style={{ fontSize:12, fontWeight:500, color: sel?'#3b82f6':T.text, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.full_name}</p>
                          <p style={{ fontSize:10, color:T.text3, margin:0, textTransform:'capitalize' }}>{u.role}</p>
                        </div>
                        {sel && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskDetailModal({ task, token, users, onClose, onSuccess, formatDate, showConfirm, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [activeTab, setActiveTab] = useState('details');
  const [checklist, setChecklist] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [childTasks, setChildTasks] = useState([]);
  const [comments, setComments] = useState([]);
  const [taskHistory, setTaskHistory] = useState([]);
  const [taskInvoice, setTaskInvoice] = useState(undefined); // null=no invoice, obj=has invoice
  const [timeLogs, setTimeLogs] = useState([]);
  const [comment, setComment] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [timeForm, setTimeForm] = useState({ hours: '', log_date: new Date().toISOString().split('T')[0], description: '' });
  const [taskData,     setTaskData]     = useState(task);
  const [approvals,    setApprovals]    = useState([]);
  const [documents,    setDocuments]    = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [docDesc,      setDocDesc]      = useState('');
  const [docClientVisible, setDocClientVisible] = useState(true);
  const [approvalNote, setApprovalNote] = useState('');
  const [showApprovalNote, setShowApprovalNote] = useState(false);
  const [showStatusOverride, setShowStatusOverride] = useState(false);
  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const [newSubtask, setNewSubtask] = useState({ subtask_name: '', description: '', assigned_to: task.assigned_to || '', due_date: '', target_date: '' });
  const commentsRef = useRef(null);
  const commentInputRef = useRef(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionedIds, setMentionedIds] = useState([]);
  const [descOpen, setDescOpen] = useState(true);
  const [descDraft, setDescDraft] = useState(task.notes || '');
  const [savingDesc, setSavingDesc] = useState(false);
  // Inline editing of a posted note (author only — enforced server-side too)
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [taskExpenses, setTaskExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ expense_date: new Date().toISOString().split('T')[0], category: '', mode: 'cash', bank_account_id: '', notes: '', amount: '', billing_status: 'unbilled' });
  const [savingExpense, setSavingExpense] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchChecklist = async () => {
    try {
      const res = await fetch(`${BASE}/api/tasks/${task.id}/checklist`, { headers });
      const data = await res.json();
      setChecklist(Array.isArray(data) ? data : []);
    } catch(e) { console.error('fetchChecklist error:', e); }
  };

  const fetchSubtasks = async () => {
    try {
      const res = await fetch(`${BASE}/api/tasks/${task.id}/subtasks`, { headers });
      const data = await res.json();
      setSubtasks(Array.isArray(data) ? data : []);
    } catch(e) { console.error('fetchSubtasks error:', e); }
  };

  // The invoice rollup (group_expenses → Total Expenses / Net Margin cards) is
  // computed server-side, so it has to be re-read whenever expenses change —
  // refreshing only the list left the cards stale until the task was reopened.
  const fetchTaskInvoice = () => fetch(`${BASE}/api/invoices/by-task/${task.id}`, { headers })
    .then(r => r.json()).then(d => setTaskInvoice(d)).catch(() => setTaskInvoice(null));

  const fetchExpenses = async () => {
    try {
      const [res] = await Promise.all([
        fetch(`${BASE}/api/tasks/${task.id}/expenses`, { headers }),
        fetchTaskInvoice(),
      ]);
      const data = await res.json();
      setTaskExpenses(Array.isArray(data) ? data : []);
    } catch(e) { console.error('fetchExpenses error:', e); }
  };

  useEffect(() => {
    fetchChecklist();
    fetchSubtasks();
    fetchChildTasks();
    fetchComments();
    fetchTimeLogs();
    fetchHistory();
    fetchApprovals();
    fetchDocuments();
    fetchExpenses(); // also loads the invoice rollup
    fetchExpenseCategories();
    fetchPaymentModes();
    fetchBankAccounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchExpenseCategories = () => fetch(`${BASE}/api/expense-categories`, { headers })
    .then(r => r.json()).then(d => setExpenseCategories(Array.isArray(d) ? d : [])).catch(() => setExpenseCategories([]));

  const fetchPaymentModes = () => fetch(`${BASE}/api/payment-modes`, { headers })
    .then(r => r.json()).then(d => setPaymentModes(Array.isArray(d) ? d.filter(m => m.is_active !== false) : [])).catch(() => setPaymentModes([]));

  const fetchBankAccounts = () => fetch(`${BASE}/api/bank/accounts`, { headers })
    .then(r => r.json()).then(d => setBankAccounts(Array.isArray(d) ? d.filter(a => a.is_active !== false) : [])).catch(() => setBankAccounts([]));

  // Inline "+ Add New Mode" quick-create (from the PaymentModeDropdown) — same
  // pattern as expense categories, posts straight to payment-modes.
  const handleCreatePaymentMode = async (name) => {
    try {
      const res = await fetch(`${BASE}/api/payment-modes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ name, value: name })
      });
      const created = await res.json().catch(() => ({}));
      if (res.ok) {
        await fetchPaymentModes();
        return created?.value ? created : null;
      }
    } catch (e) { console.error(e); }
    return null;
  };

  // Inline "+ Add New Category" quick-create (from the CategoryDropdown) — posts
  // straight to expense-categories, no detour through Settings — same pattern
  // used for the New Client "Client Type" picker.
  const handleCreateCategory = async (name) => {
    try {
      const res = await fetch(`${BASE}/api/expense-categories`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ name })
      });
      const created = await res.json().catch(() => ({}));
      if (res.ok) {
        await fetchExpenseCategories();
        return created?.name ? created : { name };
      }
    } catch (e) { console.error(e); }
    return null;
  };

  const resetExpenseForm = () => {
    setShowExpenseForm(false);
    setEditingExpenseId(null);
    setExpenseForm({ expense_date: new Date().toISOString().split('T')[0], category: '', mode: 'cash', bank_account_id: '', notes: '', amount: '', billing_status: 'unbilled' });
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.amount || !expenseForm.expense_date) return;
    setSavingExpense(true);
    try {
      const url = editingExpenseId
        ? `${BASE}/api/tasks/${task.id}/expenses/${editingExpenseId}`
        : `${BASE}/api/tasks/${task.id}/expenses`;
      const res = await fetch(url, {
        method: editingExpenseId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(expenseForm)
      });
      if (res.ok) {
        resetExpenseForm();
        fetchExpenses();
      } else {
        const err = await res.json();
        alert(err.message || 'Failed to save expense');
      }
    } catch(e) { alert('Server error'); }
    finally { setSavingExpense(false); }
  };

  const handleEditExpense = (exp) => {
    setEditingExpenseId(exp.id);
    setExpenseForm({
      expense_date: exp.expense_date ? exp.expense_date.split('T')[0] : '',
      category: exp.category || '',
      mode: exp.mode || 'cash',
      bank_account_id: exp.bank_account_id ? String(exp.bank_account_id) : '',
      notes: exp.notes || '',
      amount: exp.amount || '',
      billing_status: exp.billing_status || 'unbilled'
    });
    setShowExpenseForm(true);
  };

  const handleDeleteExpense = (exp) => {
    showConfirm('Delete Expense', `Delete this ₹${Number(exp.amount).toLocaleString('en-IN')} expense? This cannot be undone.`, async () => {
      await fetch(`${BASE}/api/tasks/${task.id}/expenses/${exp.id}`, { method: 'DELETE', headers }).catch(e => console.error(e));
      fetchExpenses();
    });
  };

  // Point 4: child tasks from the tasks table
  const fetchChildTasks = async () => {
    const res = await fetch(`${BASE}/api/tasks?parent_id=${task.id}`, { headers });
    const data = await res.json();
    setChildTasks(Array.isArray(data) ? data : []);
  };

  const fetchComments = async () => {
    const res = await fetch(`${BASE}/api/tasks/${task.id}/comments`, { headers });
    const data = await res.json();
    setComments(Array.isArray(data) ? data : []);
  };

  const fetchTimeLogs = async () => {
    const res = await fetch(`${BASE}/api/tasks/${task.id}/timelogs`, { headers });
    const data = await res.json();
    setTimeLogs(Array.isArray(data) ? data : []);
  };

  const fetchHistory = async () => {
    const res = await fetch(`${BASE}/api/tasks/${task.id}/history`, { headers });
    const data = await res.json();
    setTaskHistory(Array.isArray(data) ? data : []);
  };

  const fetchApprovals  = async()=>{try{const r=await fetch(`${BASE}/api/tasks/${task.id}/approvals`,{headers});const d=await r.json();setApprovals(Array.isArray(d)?d:[]);}catch(e){}};
  const fetchDocuments  = async()=>{try{const r=await fetch(`${BASE}/api/tasks/${task.id}/documents`,{headers});const d=await r.json();setDocuments(Array.isArray(d)?d:[]);}catch(e){}};

  const handleMarkNA = async (item) => {
    const newNA = !item.is_na;
    // Instant UI update
    setChecklist(prev => prev.map(c =>
      c.id === item.id ? { ...c, is_na: newNA, is_completed: false } : c
    ));
    try {
      const res = await fetch(`${BASE}/api/tasks/${task.id}/checklist/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ is_completed: false, is_na: newNA, item_name: item.item_name })
      });
    } catch(e) { console.error('N/A error:', e); }
  };

  const handleToggleChecklist = async (item) => {
    if (item.is_na) return;
    const newCompleted = !item.is_completed;
    // Instant UI update
    setChecklist(prev => prev.map(c =>
      c.id === item.id ? { ...c, is_completed: newCompleted } : c
    ));
    try {
      const res = await fetch(`${BASE}/api/tasks/${task.id}/checklist/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ is_completed: newCompleted, is_na: false, item_name: item.item_name })
      });
    } catch(e) { console.error('Checklist error:', e); }
  };

  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim()) return;
    await fetch(`${BASE}/api/tasks/${task.id}/checklist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ item_name: newChecklistItem, item_order: checklist.length })
    });
    setNewChecklistItem('');
    fetchChecklist();
  };

  const handleDeleteChecklist = async (id) => {
    await fetch(`${BASE}/api/tasks/${task.id}/checklist/${id}`, { method: 'DELETE', headers });
    fetchChecklist();
  };

  const handleUpdateSubtaskStatus = async (subtask, status) => {
    // Instant UI update
    setSubtasks(prev => prev.map(s =>
      s.id === subtask.id ? { ...s, status } : s
    ));
    try {
      const res = await fetch(`${BASE}/api/tasks/${task.id}/subtasks/${subtask.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          subtask_name: subtask.subtask_name,
          description: subtask.description || '',
          assigned_to: subtask.assigned_to || null,
          status,
          due_date: subtask.due_date || null,
          target_date: subtask.target_date || null,
        })
      });
    } catch(e) { console.error('Subtask error:', e); }
  };

  const handleAssignSubtaskUser = (subtask, userId) => {
    const userName = users.find(u => String(u.id) === String(userId))?.full_name || '';
    setSubtasks(prev => prev.map(s =>
      s.id === subtask.id ? { ...s, assigned_to: userId, assigned_name: userName } : s
    ));
    fetch(`${BASE}/api/tasks/${task.id}/subtasks/${subtask.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({
        subtask_name: subtask.subtask_name,
        description: subtask.description || '',
        assigned_to: userId || null,
        status: subtask.status,
        due_date: subtask.due_date || null,
        target_date: subtask.target_date || null,
      })
    }).catch(e => console.error('Subtask assign error:', e));
  };

  // Point 5: Add subtask — auto-assigned to parent user, editable
  const handleAddSubtask = async () => {
    if (!newSubtask.subtask_name.trim()) return;
    await fetch(`${BASE}/api/tasks/${task.id}/subtasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(newSubtask) // backend auto-assigns parent user if assigned_to is blank
    });
    setNewSubtask({ subtask_name: '', description: '', assigned_to: task.assigned_to || '', due_date: '', target_date: '' });
    setShowAddSubtask(false);
    fetchSubtasks();
  };

  const handleSaveNoteEdit = async (commentId) => {
    if (!editNoteText.trim() || savingNote) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${BASE}/api/tasks/${task.id}/comments/${commentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ comment: editNoteText })
      });
      if (res.ok) {
        const updated = await res.json();
        setComments(prev => prev.map(c => c.id === commentId ? { ...c, ...updated } : c));
        setEditingNoteId(null);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.message || 'Could not save the note');
      }
    } catch (e) { alert('Server error'); }
    finally { setSavingNote(false); }
  };

  const handleSendComment = async () => {
    if (!comment.trim()) return;
    await fetch(`${BASE}/api/tasks/${task.id}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ comment, mentioned_user_ids: mentionedIds })
    });
    setComment('');
    setMentionedIds([]);
    setMentionOpen(false);
    fetchComments();
    // Newest note sits at the top now, so reveal the top (not the bottom) after posting.
    setTimeout(() => commentsRef.current?.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  // @mention: candidates are the users this task is assigned to (falls back to everyone if none)
  const assignedIdSet = new Set((taskData.assigned_user_ids || '').split(',').map(s => s.trim()).filter(Boolean));
  const mentionCandidates = assignedIdSet.size > 0 ? users.filter(u => assignedIdSet.has(String(u.id))) : users;
  const filteredMentionUsers = mentionCandidates.filter(u => u.full_name?.toLowerCase().includes(mentionQuery.toLowerCase()));

  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart;
    setComment(value);
    const uptoCursor = value.slice(0, cursor);
    const match = uptoCursor.match(/@([A-Za-z ]*)$/);
    if (match) { setMentionOpen(true); setMentionQuery(match[1]); }
    else setMentionOpen(false);
  };

  const pickMention = (user) => {
    const el = commentInputRef.current;
    const cursor = el ? el.selectionStart : comment.length;
    const uptoCursor = comment.slice(0, cursor);
    const atIndex = uptoCursor.lastIndexOf('@');
    if (atIndex === -1) return;
    const newValue = comment.slice(0, atIndex) + '@' + user.full_name + ' ' + comment.slice(cursor);
    setComment(newValue);
    setMentionedIds(ids => ids.includes(user.id) ? ids : [...ids, user.id]);
    setMentionOpen(false);
    setTimeout(() => el?.focus(), 0);
  };

  // Highlight @FullName occurrences that match a real user, for rendering posted comments
  const renderCommentText = (text) => {
    const names = users.map(u => u.full_name).filter(Boolean).sort((a, b) => b.length - a.length);
    if (names.length === 0) return text;
    const escaped = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(`@(${escaped.join('|')})`, 'g');
    const parts = [];
    let lastIndex = 0, m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
      parts.push(<span key={m.index} style={{ color:'#3b82f6', fontWeight:600 }}>{m[0]}</span>);
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
  };

  const handleLogTime = async (e) => {
    e.preventDefault();
    if (!timeForm.hours) return;
    await fetch(`${BASE}/api/tasks/${task.id}/timelogs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(timeForm)
    });
    setTimeForm({ hours: '', log_date: new Date().toISOString().split('T')[0], description: '' });
    fetchTimeLogs();
  };

  const handleUpdateStatus = async (newStatus, note = '') => {
    const oldStatus = taskData.status;
    setTaskData(prev => ({ ...prev, status: newStatus }));
    setShowApprovalNote(false);
    setApprovalNote('');
    try {
      const res = await fetch(`${BASE}/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          ...taskData,
          status: newStatus,
          _old_status: oldStatus,
          approval_note: note || null,
          completed_date: newStatus === 'completed' ? new Date().toISOString().split('T')[0] : taskData.completed_date
        })
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.message || 'Failed to update status');
        setTaskData(prev => ({ ...prev, status: oldStatus })); // revert UI
        return;
      }
      const updated = await res.json();
      setTaskData(prev => ({ ...prev, ...updated }));
      await fetchApprovals();
      if (onSuccess) onSuccess(); // refresh task list
    } catch(e) { console.error('Status update error:', e); }
  };

  // Description of Services — seeded from Services Master, editable per-task (stored on tasks.notes)
  const handleSaveDescription = async () => {
    if (descDraft === (taskData.notes || '')) return;
    setSavingDesc(true);
    try {
      const res = await fetch(`${BASE}/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ ...taskData, notes: descDraft })
      });
      if (res.ok) {
        const updated = await res.json();
        setTaskData(prev => ({ ...prev, ...updated }));
        if (onSuccess) onSuccess();
      } else {
        const err = await res.json();
        alert(err.message || 'Failed to save description');
      }
    } catch(e) { alert('Server error'); }
    finally { setSavingDesc(false); }
  };

  // Point 2: Progress from live data
  const checklistDone = checklist.filter(c => c.is_completed || c.is_na).length;
  const subtaskDone = subtasks.filter(s => s.status === 'completed').length;
  const childDone = childTasks.filter(t => t.status === 'completed').length;
  const totalItems = checklist.length + subtasks.length + childTasks.length;
  const doneItems = checklistDone + subtaskDone + childDone;
  const progress = totalItems > 0 ? Math.round((doneItems / totalItems) * 100)
    : taskData.status === 'completed' ? 100 : 0;

  const totalHours = timeLogs.reduce((sum, l) => sum + Number(l.hours), 0);
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{children}</label>;

  const PR_DOT = { urgent:'#ef4444', high:'#f97316', medium:'#3b82f6', low:'#94a3b8' };
  const ST_META = {
    pending:            { color:'#d97706', bg: darkMode?'rgba(245,158,11,0.12)':'#fffbeb' },
    in_progress:        { color:'#2563eb', bg: darkMode?'rgba(59,130,246,0.12)':'#eff6ff' },
    hold:               { color:'#ea580c', bg: darkMode?'rgba(249,115,22,0.12)':'#fff7ed' },
    completed:          { color:'#16a34a', bg: darkMode?'rgba(16,185,129,0.12)':'#f0fdf4' },
    sent_for_approval:  { color:'#7c3aed', bg: darkMode?'rgba(124,58,237,0.12)':'#f5f3ff' },
    approved:           { color:'#0d9488', bg: darkMode?'rgba(13,148,136,0.12)':'#f0fdfa' },
    escalated:          { color:'#db2777', bg: darkMode?'rgba(219,39,119,0.12)':'#fdf2f8' },
    cancelled:          { color:'#64748b', bg: darkMode?'rgba(100,116,139,0.12)':'#f8fafc' },
  };

const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch(e) { return {}; } })();
  const myRole   = (currentUser?.role || '').toLowerCase().trim();
  const isAdmin   = myRole === 'admin';
  const isManager = ['manager','senior','senior associate','senior_associate'].includes(myRole);
  const isStaff   = !isAdmin && !isManager;
  const isAssigned = String(taskData.assigned_to) === String(currentUser?.id) ||
    String(taskData.assigned_user_ids || '').split(',').includes(String(currentUser?.id));

  const actionBtns = [
    // Staff/assigned user sends for approval
    {
      cond: ['in_progress','pending','hold'].includes(taskData.status) && (isStaff || isAssigned),
      label:'Send for Approval', status:'sent_for_approval',
      color:'#7c3aed', bg: darkMode?'rgba(124,58,237,0.12)':'#f5f3ff', border: darkMode?'rgba(124,58,237,0.25)':'#ddd6fe',
    },
    // Manager/Senior can Approve
    {
      cond: taskData.status==='sent_for_approval' && (isManager || isAdmin),
      label:'Approve', status:'approved',
      color:'#0d9488', bg: darkMode?'rgba(13,148,136,0.12)':'#f0fdfa', border: darkMode?'rgba(13,148,136,0.25)':'#99f6e4',
    },
    // Manager/Senior can Escalate to Admin
    {
      cond: taskData.status==='sent_for_approval' && isManager,
      label:'Escalate to Admin', status:'escalated',
      color:'#db2777', bg: darkMode?'rgba(219,39,119,0.12)':'#fdf2f8', border: darkMode?'rgba(219,39,119,0.25)':'#fbcfe8',
    },
    // Manager or Admin can Send Back
    {
      cond: ['sent_for_approval','escalated'].includes(taskData.status) && (isManager || isAdmin),
      label:'Send Back', status:'in_progress',
      color:'#ea580c', bg: darkMode?'rgba(249,115,22,0.12)':'#fff7ed', border: darkMode?'rgba(249,115,22,0.25)':'#fed7aa',
    },
    // Only Admin can approve escalated tasks
    {
      cond: taskData.status==='escalated' && isAdmin,
      label:'Admin Approve', status:'approved',
      color:'#0d9488', bg: darkMode?'rgba(13,148,136,0.12)':'#f0fdfa', border: darkMode?'rgba(13,148,136,0.25)':'#99f6e4',
    },
    // Admin/Manager marks complete
    {
      cond: taskData.status==='approved' && (isAdmin || isManager),
      label:'Mark Complete', status:'completed',
      color:'#16a34a', bg: darkMode?'rgba(16,185,129,0.12)':'#f0fdf4', border: darkMode?'rgba(16,185,129,0.25)':'#bbf7d0',
    },
  ];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:12 }}>
      <div style={{ background:T.bg2, borderRadius:20, display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)', width:'100%', maxWidth:1440, height:'94vh' }}>

        {/* HEADER */}
        <div style={{ padding:'16px 24px', borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.bg2, borderRadius:'20px 20px 0 0' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16 }}>

            {/* Left */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
                <button onClick={() => navigator.clipboard.writeText(taskData.task_number)} title="Copy task ID"
                  style={{ fontSize:11, fontFamily:'monospace', fontWeight:700, color:'#3b82f6', background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.25)':'#bfdbfe'}`, padding:'3px 10px', borderRadius:7, cursor:'pointer' }}>
                  {taskData.task_number}
                </button>
                {taskData.parent_task_title && (
                  <span style={{ fontSize:11, background: darkMode?'rgba(99,102,241,0.12)':'#eef2ff', color:'#6366f1', border:`1px solid ${darkMode?'rgba(99,102,241,0.25)':'#c7d2fe'}`, padding:'3px 8px', borderRadius:7 }}>
                    ↳ {taskData.parent_task_title}
                  </span>
                )}
                {(() => { const sm = ST_META[taskData.status]||{color:T.text3,bg:T.bg3}; return <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, color:sm.color, background:sm.bg }}>{STATUS_LABELS[taskData.status]}</span>; })()}
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ width:8, height:8, borderRadius:'50%', background: PR_DOT[taskData.priority]||T.text3, flexShrink:0 }}/>
                  <span style={{ fontSize:11, color:T.text3, textTransform:'capitalize' }}>{taskData.priority} priority</span>
                </div>
              </div>
              <h2 style={{ fontSize:16, fontWeight:700, color:T.text, margin:0, lineHeight:1.3 }}>{taskData.title}</h2>
              <div style={{ display:'flex', alignItems:'center', gap:16, marginTop:8, flexWrap:'wrap' }}>
                {taskData.target_date && <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#f97316' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                  Target: {formatDate(taskData.target_date)}
                </span>}
                {taskData.due_date && (() => {
                  const isDueOverdue = new Date(taskData.due_date) < new Date() && taskData.status !== 'completed';
                  const dueColor = isDueOverdue ? '#ef4444' : '#2563eb';
                  return (
                    <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600, color:dueColor }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      Due: {formatDate(taskData.due_date)}{isDueOverdue ? ' (overdue)' : ''}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Right actions */}
            <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, flexWrap:'wrap' }}>
              {taskInvoice !== undefined && (
                taskInvoice ? (
                  <button onClick={() => { localStorage.setItem('openInvoiceId', String(taskInvoice.id)); window.dispatchEvent(new CustomEvent('navigateTo',{detail:{page:'finance',tab:'invoices'}})); }}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:600, color:'#16a34a', background: darkMode?'rgba(16,185,129,0.12)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(16,185,129,0.25)':'#bbf7d0'}`, cursor:'pointer' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                    {taskInvoice.invoice_number}
                  </button>
                ) : taskData.is_billable && taskData.billing_status==='unbilled' && (
                  <button onClick={() => { localStorage.setItem('pendingInvoice', JSON.stringify({source:'task',client_id:taskData.client_id,task_id:taskData.id,task_title:taskData.title||taskData.service_name,client_name:taskData.client_name})); window.dispatchEvent(new CustomEvent('navigateTo',{detail:{page:'finance'}})); }}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:600, color:'#2563eb', background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.25)':'#bfdbfe'}`, cursor:'pointer' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Create Invoice
                  </button>
                )
              )}
              {actionBtns.filter(b => b.cond).map(b => (
                <button key={b.label} onClick={() => handleUpdateStatus(b.status)}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:600, color:b.color, background:b.bg, border:`1px solid ${b.border}`, cursor:'pointer' }}>
                  {b.label}
                </button>
              ))}
              {isAdmin && (
                <div style={{ position:'relative' }}>
                  <p style={{ fontSize:10, color:T.text3, margin:'0 0 3px' }}>Override Status</p>
                  <button type="button" onClick={() => setShowStatusOverride(o => !o)}
                    style={{ display:'flex', alignItems:'center', gap:8, border:`1px solid ${showStatusOverride?'#3b82f6':T.inputBorder}`, borderRadius:8, padding:'5px 10px', fontSize:12, fontWeight:600, outline:'none', background:T.inputBg, color:T.text, cursor:'pointer', boxShadow: showStatusOverride?'0 0 0 3px rgba(59,130,246,0.15)':'none', transition:'all 0.15s' }}>
                    <span style={{ width:7, height:7, borderRadius:'50%', background:(ST_META[taskData.status]||{}).color||T.text3, flexShrink:0 }}/>
                    {STATUS_LABELS[taskData.status] || taskData.status}
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2.5" style={{ flexShrink:0, transform: showStatusOverride?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  {showStatusOverride && (
                    <div style={{ position:'absolute', right:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:70, width:210, padding:6 }}
                      onMouseLeave={() => setShowStatusOverride(false)}>
                      {Object.entries(STATUS_LABELS).map(([v,l]) => {
                        const sel = taskData.status === v;
                        const meta = ST_META[v] || { color:T.text3, bg:T.bg3 };
                        return (
                          <button key={v} type="button" onClick={() => { handleUpdateStatus(v); setShowStatusOverride(false); }}
                            style={{ width:'100%', textAlign:'left', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:12.5, fontWeight:600, background: sel?meta.bg:'transparent', color: sel?meta.color:T.text2, transition:'background 0.1s' }}
                            onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                            onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                            <span style={{ width:7, height:7, borderRadius:'50%', background:meta.color, flexShrink:0 }}/>
                            <span style={{ flex:1 }}>{l}</span>
                            {sel && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={meta.color} strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <button onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', border:'none', background:T.bg3, color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', marginLeft:4 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ marginTop:14 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
              <span style={{ fontSize:12, fontWeight:600, color:T.text2 }}>Overall Progress</span>
              <span style={{ fontSize:11, color:T.text3 }}>{totalItems>0 ? `${doneItems} of ${totalItems} items · ${progress}%` : taskData.status==='completed'?'Task Completed':'No checklist / subtasks'}</span>
            </div>
            <div style={{ width:'100%', background:T.bg3, borderRadius:20, height:8 }}>
              <div style={{ height:8, borderRadius:20, background: progress===100?'#10b981':progress>50?'#3b82f6':'#60a5fa', width:`${progress}%`, transition:'width 0.5s' }}/>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, flexShrink:0, overflowX:'auto', scrollbarWidth:'none', background:T.bg2, padding:'0 8px' }}>
          {[
            { key:'details',    label:'Details' },
            { key:'checklist',  label:`Checklist (${checklistDone}/${checklist.length})` },
            { key:'subtasks',   label:`Subtasks (${subtaskDone}/${subtasks.length})` },
            { key:'child_tasks',label:`Sub-Tasks (${childDone}/${childTasks.length})` },
            { key:'time',       label:`Time Log (${totalHours}h)` },
            { key:'history',    label:`History (${taskHistory.length})` },
            { key:'approvals',  label:`Approvals (${approvals.length})` },
            { key:'documents',  label:`Documents (${documents.length})` },
            { key:'expenses',   label:`Expenses (${taskExpenses.length})` },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding:'12px 16px', fontSize:13, fontWeight:600, whiteSpace:'nowrap',
              border:'none', borderBottom: activeTab===tab.key?'2px solid #3b82f6':'2px solid transparent',
              color: activeTab===tab.key?'#3b82f6':T.text3, background:'none', cursor:'pointer', transition:'all 0.15s',
            }}
            onMouseEnter={e => { if(activeTab!==tab.key) e.currentTarget.style.color=T.text2; }}
            onMouseLeave={e => { if(activeTab!==tab.key) e.currentTarget.style.color=T.text3; }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB CONTENT */}
        <div style={{ flex:1, overflowY:'auto', padding:24, background:T.bg3 }}>

          {/* DETAILS */}
          {activeTab==='details' && (<>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20 }}>
                  <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 14px' }}>Task Information</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {[
                      { label:'Task Number', value: taskData.task_number },
                      { label:'Client',      value: taskData.client_name, linkToClient: true },
                      { label:'File Number', value: taskData.file_number, linkToClient: true },
                      { label:'Service',     value: taskData.service_name },
                      { label:'Group',       value: taskData.task_group_name },
                      { label:'Parent Task', value: taskData.parent_task_title },
                    ].map(item => item.value ? (
                      <div key={item.label} style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                        <span style={{ fontSize:11, color:T.text3, width:100, flexShrink:0 }}>{item.label}</span>
                        {item.linkToClient && taskData.client_id ? (
                          <button type="button"
                            onClick={() => {
                              // So Client Dashboard's close button returns here (this exact task)
                              // instead of landing on the plain Clients list.
                              localStorage.setItem('clientDashboardReturnTaskId', String(taskData.id));
                              window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page:'clients', clientId: taskData.client_id } }));
                            }}
                            style={{ fontSize:13, color:'#2563eb', fontWeight:500, background:'none', border:'none', padding:0, cursor:'pointer', textAlign:'left' }}
                            onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                            onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                            {item.value}
                          </button>
                        ) : (
                          <span style={{ fontSize:13, color:T.text, fontWeight:500 }}>{item.value}</span>
                        )}
                      </div>
                    ) : null)}
                  </div>
                </div>

                {/* Description of Services — seeded from Services Master, collapsible & editable per-task */}
                <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20 }}>
                  <div onClick={() => setDescOpen(o => !o)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', marginBottom: descOpen?14:0 }}>
                    <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Description of Services</p>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2.5" style={{ flexShrink:0, transform: descOpen?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                  </div>
                  {descOpen && (
                    <div>
                      <textarea rows="8" value={descDraft} onChange={e => setDescDraft(e.target.value)}
                        placeholder="No description — pulled from Services Master when the task is created. Edit here for this task only."
                        style={{ width:'100%', fontSize:12.5, lineHeight:1.6, outline:'none', resize:'vertical', color:T.text, background:T.inputBg, border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:10, boxSizing:'border-box' }} />
                      {descDraft !== (taskData.notes || '') && (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:8, marginTop:8 }}>
                          <button type="button" onClick={() => setDescDraft(taskData.notes || '')}
                            style={{ padding:'6px 14px', borderRadius:8, border:`1px solid ${T.border2}`, background:'transparent', color:T.text2, fontSize:11.5, fontWeight:600, cursor:'pointer' }}>
                            Cancel
                          </button>
                          <button type="button" onClick={handleSaveDescription} disabled={savingDesc}
                            style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:11.5, fontWeight:600, cursor:'pointer', opacity: savingDesc?0.6:1 }}>
                            {savingDesc ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20, alignSelf:'start' }}>
                <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 14px' }}>Dates & Team</p>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {(taskData.assigned_team_data || taskData.assigned_names || taskData.assigned_name) && (
                    <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                      <span style={{ fontSize:11, color:T.text3, width:100, flexShrink:0, marginTop:3 }}>Assigned To</span>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                        {taskData.assigned_team_data ? (
                          taskData.assigned_team_data.split(',').map(t => {
                            const [tid, tname, tavatar] = t.split(':');
                            if (!tname) return null;
                            return (
                              <span key={tid} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 10px 3px 3px', borderRadius:20, background:T.bg3, border:`1px solid ${T.border}` }}>
                                <span style={{ width:20, height:20, borderRadius:'50%', overflow:'hidden', background: tavatar?'transparent':'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:9.5, fontWeight:700, flexShrink:0 }}>
                                  {tavatar
                                    ? <img src={`${BASE}${tavatar}`} alt={tname} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                                    : tname.charAt(0).toUpperCase()}
                                </span>
                                <span style={{ fontSize:12.5, fontWeight:500, color:T.text }}>{tname}</span>
                              </span>
                            );
                          })
                        ) : (
                          <span style={{ fontSize:13, color:T.text, fontWeight:500 }}>{taskData.assigned_names||taskData.assigned_name}</span>
                        )}
                      </div>
                    </div>
                  )}
                  {[
                    { label:'Due Date',      value: formatDate(taskData.due_date) },
                    { label:'Target Date',   value: formatDate(taskData.target_date) },
                    { label:'Completed',     value: formatDate(taskData.completed_date) },
                    { label:'Est. Hours',    value: taskData.estimated_hours?`${taskData.estimated_hours}h`:'—' },
                    { label:'Logged Hours',  value: `${totalHours}h` },
                  ].map(item => item.value ? (
                    <div key={item.label} style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                      <span style={{ fontSize:11, color:T.text3, width:100, flexShrink:0 }}>{item.label}</span>
                      <span style={{ fontSize:13, color:T.text, fontWeight:500 }}>{item.value}</span>
                    </div>
                  ) : null)}
                </div>
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20 }}>
                  <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 14px' }}>Billing</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:12, color:T.text3 }}>Is Billable</span>
                      <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background: taskData.is_billable?(darkMode?'rgba(16,185,129,0.12)':'#f0fdf4'):(darkMode?'rgba(100,116,139,0.12)':'#f8fafc'), color: taskData.is_billable?'#16a34a':T.text3 }}>
                        {taskData.is_billable?'Yes':'No'}
                      </span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:12, color:T.text3 }}>Billing Status</span>
                      <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20,
                        background: taskData.billing_status==='billed'?(darkMode?'rgba(16,185,129,0.12)':'#f0fdf4'):taskData.billing_status==='unbilled'?(darkMode?'rgba(249,115,22,0.12)':'#fff7ed'):(darkMode?'rgba(100,116,139,0.12)':'#f8fafc'),
                        color: taskData.billing_status==='billed'?'#16a34a':taskData.billing_status==='unbilled'?'#ea580c':T.text3
                      }}>
                        {taskData.billing_status==='billed' ? 'Billed' : taskData.billing_status==='unbilled' ? 'Unbilled' : 'Not Billable'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes & Discussion — inline comment thread with @mention tagging of assignees */}
                <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20 }}>
                  <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 10px' }}>
                    Notes {comments.length > 0 ? `(${comments.length})` : ''}
                  </p>
                  {comments.length > 0 && (
                    <div ref={commentsRef} style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12, maxHeight:220, overflowY:'auto' }}>
                      {/* Newest first — the latest note is what people came to read */}
                      {[...comments].sort((a, b) => (new Date(b.created_at) - new Date(a.created_at)) || (b.id - a.id)).map(c => (
                        <div key={c.id} style={{ background:T.bg3, borderRadius:10, padding:'10px 12px' }}>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                            <span style={{ fontSize:12, fontWeight:600, color:T.text }}>{c.user_name}</span>
                            <span style={{ display:'flex', alignItems:'center', gap:6, fontSize:10, color:T.text3 }}>
                              {new Date(c.created_at).toLocaleString('en-IN',{timeZone:'Asia/Kolkata',day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true})}
                              {Number(c.created_by) === Number(currentUser?.id) && editingNoteId !== c.id && (
                                <button type="button" title="Edit note" onClick={() => { setEditingNoteId(c.id); setEditNoteText(c.comment); }}
                                  style={{ width:20, height:20, borderRadius:5, border:'none', background:'transparent', color:T.text3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}
                                  onMouseEnter={e => { e.currentTarget.style.background=T.bg2; e.currentTarget.style.color='#3b82f6'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color=T.text3; }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                </button>
                              )}
                            </span>
                          </div>
                          {editingNoteId === c.id ? (
                            <div>
                              <textarea autoFocus rows="3" value={editNoteText} onChange={e => setEditNoteText(e.target.value)}
                                onKeyDown={e => { if (e.key==='Escape') setEditingNoteId(null); if (e.key==='Enter' && e.ctrlKey) handleSaveNoteEdit(c.id); }}
                                style={{ width:'100%', fontSize:12.5, outline:'none', resize:'vertical', color:T.text, background:T.inputBg, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:8, boxSizing:'border-box' }} />
                              <div style={{ display:'flex', justifyContent:'flex-end', gap:6, marginTop:6 }}>
                                <button type="button" onClick={() => setEditingNoteId(null)}
                                  style={{ padding:'4px 12px', borderRadius:7, border:`1px solid ${T.border2}`, background:'transparent', color:T.text2, fontSize:11, fontWeight:600, cursor:'pointer' }}>Cancel</button>
                                <button type="button" onClick={() => handleSaveNoteEdit(c.id)} disabled={savingNote || !editNoteText.trim()}
                                  style={{ padding:'4px 12px', borderRadius:7, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:11, fontWeight:600, cursor:'pointer', opacity:(savingNote || !editNoteText.trim())?0.5:1 }}>
                                  {savingNote ? 'Saving...' : 'Save'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p style={{ fontSize:12.5, color:T.text2, lineHeight:1.5, margin:0, whiteSpace:'pre-wrap' }}>{renderCommentText(c.comment)}</p>
                          )}
                          {c.edited_at && editingNoteId !== c.id && (
                            <p style={{ fontSize:10, fontStyle:'italic', color:T.text3, margin:'4px 0 0' }}>
                              edited by {c.edited_by_name || c.user_name} · {new Date(c.edited_at).toLocaleString('en-IN',{timeZone:'Asia/Kolkata',day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true})}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ position:'relative' }}>
                    <textarea ref={commentInputRef} rows="3" placeholder="Write a note... use @ to tag an assignee"
                      value={comment} onChange={handleCommentChange}
                      onKeyPress={e => e.key==='Enter' && e.ctrlKey && handleSendComment()}
                      style={{ width:'100%', fontSize:12.5, outline:'none', resize:'none', color:T.text, background:T.inputBg, border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:10, boxSizing:'border-box' }} />
                    {mentionOpen && filteredMentionUsers.length > 0 && (
                      <div style={{ position:'absolute', left:0, bottom:'calc(100% + 4px)', width:'100%', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:10, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, maxHeight:160, overflowY:'auto' }}>
                        {filteredMentionUsers.map(u => (
                          <button key={u.id} type="button" onClick={() => pickMention(u)}
                            style={{ width:'100%', textAlign:'left', padding:'8px 12px', border:'none', cursor:'pointer', fontSize:12.5, fontWeight:500, background:'transparent', color:T.text, display:'flex', alignItems:'center', gap:8 }}
                            onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                            <div style={{ width:20, height:20, borderRadius:'50%', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:9, fontWeight:700, flexShrink:0 }}>
                              {u.full_name?.charAt(0)?.toUpperCase()||'?'}
                            </div>
                            {u.full_name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', marginTop:8 }}>
                    <button type="button" onClick={handleSendComment} disabled={!comment.trim()} style={{
                      padding:'6px 14px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff',
                      fontSize:11.5, fontWeight:600, cursor:'pointer', opacity: !comment.trim()?0.4:1,
                    }}>Post</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Creation log — who/when/how this task came to exist (Created-by footer) */}
            {(() => {
              const src = taskData.creation_source || 'manual';
              const isAuto = src === 'recurring';
              const viaLabel = { quotation:'via Quotation acceptance', lead_conversion:'via Lead conversion' }[src];
              const when = taskData.created_at
                ? new Date(taskData.created_at).toLocaleString('en-IN', { timeZone:'Asia/Kolkata', day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true })
                : null;
              const creatorName = isAuto ? 'Recurring Task Automation' : (taskData.created_by_name || 'Unknown user');
              return (
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginTop:20, padding:'0 4px', fontSize:12, color:T.text3 }}>
                  <span>Created by</span>
                  <span style={{ width:24, height:24, borderRadius:'50%', overflow:'hidden', flexShrink:0, display:'inline-flex', alignItems:'center', justifyContent:'center',
                    background: (!isAuto && taskData.created_by_avatar) ? 'transparent' : (isAuto ? (darkMode?'rgba(99,102,241,0.2)':'#eef2ff') : 'linear-gradient(135deg,#0f1f45,#1a3a6b)'),
                    color: isAuto ? '#6366f1' : '#fff', fontSize:10, fontWeight:700 }}>
                    {isAuto
                      ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                      : taskData.created_by_avatar
                        ? <img src={`http://localhost:5000${taskData.created_by_avatar}`} alt={creatorName} style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center 20%' }} />
                        : creatorName.charAt(0).toUpperCase()}
                  </span>
                  <span style={{ fontWeight:600, color:T.text2 }}>{creatorName}</span>
                  {viaLabel && <span style={{ fontSize:11, padding:'1px 8px', borderRadius:20, background:T.bg2, border:`1px solid ${T.border}` }}>{viaLabel}</span>}
                  {when && (
                    <>
                      <span>on</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <span>{when}</span>
                    </>
                  )}
                </div>
              );
            })()}
          </>)}

          {/* CHECKLIST */}
          {activeTab==='checklist' && (
            <div style={{ maxWidth:640 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Document Checklist</h3>
                  <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>{checklistDone} of {checklist.length} items completed</p>
                </div>
                {checklist.length>0 && (
                  <span style={{ fontSize:12, color:T.text3, background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, padding:'4px 12px' }}>
                    {Math.round((checklistDone/checklist.length)*100)}% done
                  </span>
                )}
              </div>
              <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, overflow:'hidden', marginBottom:14 }}>
                {checklist.length===0 ? (
                  <div style={{ padding:'40px 0', textAlign:'center', color:T.text3, fontSize:13 }}>No checklist items. Add below or configure in Services Master.</div>
                ) : checklist.map((item,index) => (
                  <div key={item.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', borderBottom:`1px solid ${T.border}`, background: item.is_completed?(darkMode?'rgba(16,185,129,0.06)':'#f0fdf4'):'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background=item.is_completed?(darkMode?'rgba(16,185,129,0.08)':'#dcfce7'):T.bg3}
                    onMouseLeave={e => e.currentTarget.style.background=item.is_completed?(darkMode?'rgba(16,185,129,0.06)':'#f0fdf4'):'transparent'}>
                    <button type="button" onClick={() => !item.is_na && handleToggleChecklist(item)}
                      style={{ width:20, height:20, borderRadius:5, border:`2px solid ${item.is_na?T.border2:item.is_completed?'#10b981':T.border2}`, background: item.is_na?T.bg3:item.is_completed?'#10b981':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, cursor: item.is_na?'not-allowed':'pointer', transition:'all 0.15s' }}>
                      {item.is_completed && !item.is_na && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                      {item.is_na && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
                    </button>
                    <span style={{ fontSize:11, color:T.text3, width:18, flexShrink:0 }}>{index+1}</span>
                    <span style={{ fontSize:13, flex:1, color: item.is_na?T.text3:item.is_completed?'#10b981':T.text, textDecoration: item.is_na?'line-through':'none' }}>{item.item_name}</span>
                    {item.is_completed && item.completed_by_name && <span style={{ fontSize:11, color:'#10b981', fontWeight:600 }}>{item.completed_by_name}</span>}
                    <button type="button" onClick={() => handleMarkNA(item)}
                      style={{ fontSize:11, padding:'2px 8px', borderRadius:6, fontWeight:600, cursor:'pointer', border:'none', flexShrink:0,
                        background: item.is_na?(darkMode?'rgba(100,116,139,0.2)':'#e2e8f0'):(darkMode?'rgba(100,116,139,0.1)':'#f1f5f9'),
                        color: item.is_na?T.text2:T.text3 }}>
                      N/A
                    </button>
                    <button type="button" onClick={() => handleDeleteChecklist(item.id)}
                      style={{ width:26, height:26, borderRadius:7, border:'none', background:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                      onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                      onMouseLeave={e => e.currentTarget.style.background='none'}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <input type="text" placeholder="Add checklist item... (Press Enter)"
                  value={newChecklistItem} onChange={e => setNewChecklistItem(e.target.value)}
                  onKeyPress={e => e.key==='Enter' && handleAddChecklistItem()}
                  style={{ flex:1, ...is }} />
                <button onClick={handleAddChecklistItem} style={{ padding:'9px 20px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>+ Add</button>
              </div>
            </div>
          )}

          {/* SUBTASKS */}
          {activeTab==='subtasks' && (
            <div style={{ maxWidth:720 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Workflow Subtasks</h3>
                  <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>{subtaskDone} of {subtasks.length} completed</p>
                </div>
                <button onClick={() => setShowAddSubtask(!showAddSubtask)} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add Subtask
                </button>
              </div>
              {showAddSubtask && (
                <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, padding:20, marginBottom:16, boxShadow: darkMode?'0 4px 16px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.06)' }}>
                  <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14 }}>New Subtask</p>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                    <div style={{ gridColumn:'span 2' }}>
                      <SL>Subtask Name *</SL>
                      <input type="text" placeholder="Enter subtask name" value={newSubtask.subtask_name}
                        onChange={e => setNewSubtask(f => ({ ...f, subtask_name: e.target.value }))} style={is} />
                    </div>
                    <div>
                      <SL>Assigned To</SL>
                      <select value={newSubtask.assigned_to} onChange={e => setNewSubtask(f => ({ ...f, assigned_to: e.target.value }))} style={is}>
                        <option value="">Auto (Parent Assignee)</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                      </select>
                    </div>
                    <div>
                      <SL>Description</SL>
                      <input type="text" placeholder="Optional description" value={newSubtask.description||''}
                        onChange={e => setNewSubtask(f => ({ ...f, description: e.target.value }))} style={is} />
                    </div>
                    <SmartDatePicker label="Due Date" value={newSubtask.due_date} onChange={v => setNewSubtask(f => ({ ...f, due_date: v }))} darkMode={darkMode} T={T} />
                    <SmartDatePicker label="Target Date" value={newSubtask.target_date} onChange={v => setNewSubtask(f => ({ ...f, target_date: v }))} darkMode={darkMode} T={T} />
                  </div>
                  <div style={{ display:'flex', gap:10, marginTop:16 }}>
                    <button onClick={() => setShowAddSubtask(false)} style={{ padding:'8px 18px', borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
                    <button onClick={handleAddSubtask} style={{ marginLeft:'auto', padding:'8px 20px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>Create Subtask</button>
                  </div>
                </div>
              )}
              {subtasks.length===0 && !showAddSubtask ? (
                <div style={{ padding:'40px 0', textAlign:'center', background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}` }}>
                  <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No subtasks yet</p>
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Configure in Services Master or add manually above</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {subtasks.map((subtask,index) => (
                    <SubtaskRow key={subtask.id} subtask={subtask} index={index}
                      users={users} onStatusChange={handleUpdateSubtaskStatus}
                      onAssignUser={handleAssignSubtaskUser} STATUS_COLORS={STATUS_COLORS}
                      darkMode={darkMode} T={T} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CHILD TASKS */}
          {activeTab==='child_tasks' && (
            <div>
              <div style={{ marginBottom:16 }}>
                <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Sub-Tasks (as Full Tasks)</h3>
                <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>{childDone} of {childTasks.length} completed · visible in main Task list</p>
              </div>
              {childTasks.length===0 ? (
                <div style={{ padding:'48px 0', textAlign:'center', background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}` }}>
                  <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No sub-tasks yet</p>
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Create tasks from the Task list and link this as the parent task</p>
                </div>
              ) : (
                <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, overflow:'hidden' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                      <tr>
                        {['Task ID','Task','Assigned To','Due Date','Progress','Status'].map(h => (
                          <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {childTasks.map((ct,i) => {
                        const prog = getProgress(ct);
                        const sm = ST_META[ct.status]||{color:T.text3,bg:T.bg3};
                        return (
                          <tr key={ct.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff') }}
                            onMouseEnter={e => e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                            onMouseLeave={e => e.currentTarget.style.background=darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')}>
                            <td style={{ padding:'10px 16px' }}>
                              <span style={{ fontFamily:'monospace', fontSize:11, fontWeight:700, color:'#3b82f6', background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, padding:'2px 7px', borderRadius:5 }}>{ct.task_number}</span>
                            </td>
                            <td style={{ padding:'10px 16px', fontSize:13, fontWeight:500, color:T.text }}>{ct.title}</td>
                            <td style={{ padding:'10px 16px', fontSize:12, color:T.text3 }}>{ct.assigned_name||'—'}</td>
                            <td style={{ padding:'10px 16px', fontSize:12, color:T.text3, whiteSpace:'nowrap' }}>
                              {ct.due_date ? new Date(ct.due_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                            </td>
                            <td style={{ padding:'10px 16px' }}>
                              <div style={{ width:64 }}>
                                <div style={{ width:'100%', background:T.bg3, borderRadius:20, height:6 }}>
                                  <div style={{ height:6, borderRadius:20, background: prog.pct===100?'#10b981':'#3b82f6', width:`${prog.pct}%` }}/>
                                </div>
                                <p style={{ fontSize:10, color:T.text3, margin:'3px 0 0' }}>{prog.pct}%</p>
                              </div>
                            </td>
                            <td style={{ padding:'10px 16px' }}>
                              <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, color:sm.color, background:sm.bg }}>{STATUS_LABELS[ct.status]}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TIME LOG */}
          {activeTab==='time' && (
            <div style={{ maxWidth:640 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
                <div>
                  <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Time Tracking</h3>
                  <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>{totalHours}h logged{taskData.estimated_hours?` · ${taskData.estimated_hours}h estimated`:''}</p>
                </div>
                {taskData.estimated_hours && (
                  <div style={{ textAlign:'right' }}>
                    <p style={{ fontSize:11, color:T.text3, margin:0 }}>Utilization</p>
                    <p style={{ fontSize:14, fontWeight:700, color: totalHours>taskData.estimated_hours?'#ef4444':'#16a34a', margin:0 }}>
                      {Math.round((totalHours/taskData.estimated_hours)*100)}%
                    </p>
                  </div>
                )}
              </div>
              <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:20, marginBottom:20 }}>
                <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:16 }}>Log Time</p>
                <form onSubmit={handleLogTime}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:14 }}>
                    <div>
                      <SL>Hours *</SL>
                      <input type="number" step="0.5" min="0.5" placeholder="e.g. 2.5" required
                        value={timeForm.hours} onChange={e => setTimeForm(f => ({ ...f, hours: e.target.value }))} style={is} />
                    </div>
                    <SmartDatePicker label="Date" value={timeForm.log_date} onChange={v => setTimeForm(f => ({ ...f, log_date: v }))} darkMode={darkMode} T={T} />
                    <div>
                      <SL>Description</SL>
                      <input type="text" placeholder="What did you work on?"
                        value={timeForm.description} onChange={e => setTimeForm(f => ({ ...f, description: e.target.value }))} style={is} />
                    </div>
                  </div>
                  <div style={{ display:'flex', justifyContent:'flex-end' }}>
                    <button type="submit" style={{ padding:'8px 20px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>Log Time</button>
                  </div>
                </form>
              </div>
              {timeLogs.length>0 && (
                <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, overflow:'hidden' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                      <tr>
                        {['Date','Hours','Description','Logged By'].map(h => (
                          <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {timeLogs.map((log,i) => (
                        <tr key={log.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff') }}>
                          <td style={{ padding:'10px 16px', fontSize:12, color:T.text2, whiteSpace:'nowrap' }}>
                            {new Date(log.log_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                          </td>
                          <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color:'#3b82f6' }}>{log.hours}h</td>
                          <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{log.description||'—'}</td>
                          <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{log.logged_by_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* HISTORY */}
          {activeTab==='history' && (
            <div style={{ maxWidth:640 }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:'0 0 16px' }}>Task History & Audit Log</h3>
              {taskHistory.length===0 ? (
                <div style={{ padding:'48px 0', textAlign:'center', background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}` }}>
                  <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No history recorded yet</p>
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>All status changes, assignments and edits will appear here</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {taskHistory.map(h => (
                    <div key={h.id} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:16, display:'flex', alignItems:'flex-start', gap:12 }}>
                      <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:12, fontWeight:700, flexShrink:0 }}>
                        {h.user_name?.charAt(0)?.toUpperCase()||'?'}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                          <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{h.user_name||'System'}</span>
                          <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', color:'#3b82f6' }}>{h.action}</span>
                          {h.field_name && <span style={{ fontSize:11, color:T.text3, fontStyle:'italic' }}>{h.field_name}</span>}
                        </div>
                        {h.old_value && h.new_value && (
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, flexWrap:'wrap' }}>
                            <span style={{ fontSize:11, textDecoration:'line-through', background: darkMode?'rgba(239,68,68,0.1)':'#fef2f2', color:'#ef4444', padding:'2px 8px', borderRadius:5, border:`1px solid ${darkMode?'rgba(239,68,68,0.2)':'#fecaca'}` }}>{h.old_value}</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                            <span style={{ fontSize:11, background: darkMode?'rgba(16,185,129,0.1)':'#f0fdf4', color:'#16a34a', padding:'2px 8px', borderRadius:5, border:`1px solid ${darkMode?'rgba(16,185,129,0.2)':'#bbf7d0'}`, fontWeight:600 }}>{h.new_value}</span>
                          </div>
                        )}
                        {!h.old_value && h.new_value && <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>{h.new_value}</p>}
                        <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>
                          {new Date(h.created_at).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* APPROVALS */}
          {activeTab==='approvals' && (
            <div style={{ maxWidth:640 }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:'0 0 16px' }}>Approval Trail</h3>
              {(() => {
                const sm = ST_META[taskData.status]||{color:T.text3,bg:T.bg3};
                return (
                  <div style={{ display:'flex', alignItems:'center', gap:12, padding:16, borderRadius:12, background:sm.bg, border:`1px solid ${sm.color}33`, marginBottom:20 }}>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:13, fontWeight:700, color:sm.color, margin:0 }}>Current Status: {STATUS_LABELS[taskData.status]}</p>
                      <p style={{ fontSize:11, color:sm.color, opacity:0.75, marginTop:3 }}>
                        {taskData.status==='pending'&&'Task not yet started'}
                        {taskData.status==='in_progress'&&'Work in progress by assigned team'}
                        {taskData.status==='sent_for_approval'&&'Waiting for Senior Associate review'}
                        {taskData.status==='escalated'&&'Escalated — waiting for Admin/Head approval'}
                        {taskData.status==='approved'&&'Approved — ready to be marked complete'}
                        {taskData.status==='completed'&&'Task fully completed'}
                      </p>
                    </div>
                  </div>
                );
              })()}
              {approvals.length===0 ? (
                <div style={{ padding:'40px 0', textAlign:'center', background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}` }}>
                  <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No approval actions yet</p>
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Actions appear here when task is sent for approval</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {approvals.map(a => {
                    const DOT = { 'Sent for Approval':'#7c3aed', 'Escalated to Admin':'#db2777', 'Approved':'#0d9488', 'Marked Completed':'#16a34a' };
                    const col = DOT[a.action]||T.text3;
                    const smFrom = ST_META[a.from_status]||{color:T.text3,bg:T.bg3};
                    const smTo   = ST_META[a.to_status]||{color:T.text3,bg:T.bg3};
                    return (
                      <div key={a.id} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:16, borderRadius:12, background:T.bg2, border:`1px solid ${T.cardBorder}` }}>
                        <div style={{ width:10, height:10, borderRadius:'50%', background:col, flexShrink:0, marginTop:4 }}/>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                            <div>
                              <span style={{ fontSize:13, fontWeight:700, color:T.text }}>{a.action}</span>
                              {a.actioned_by_name && <span style={{ fontSize:11, color:T.text3, marginLeft:8 }}>by {a.actioned_by_name}</span>}
                            </div>
                            <span style={{ fontSize:11, color:T.text3 }}>
                              {new Date(a.created_at).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                            </span>
                          </div>
                          {a.from_status && a.to_status && (
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
                              <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, color:smFrom.color, background:smFrom.bg }}>{STATUS_LABELS[a.from_status]||a.from_status}</span>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                              <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, color:smTo.color, background:smTo.bg }}>{STATUS_LABELS[a.to_status]||a.to_status}</span>
                            </div>
                          )}
                          {a.note && <p style={{ fontSize:11, color:T.text3, marginTop:8, fontStyle:'italic' }}>"{a.note}"</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* DOCUMENTS */}
          {activeTab==='documents' && (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
                <div>
                  <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Task Documents</h3>
                  <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>{documents.length} file{documents.length!==1?'s':''} · Visible to client on portal</p>
                </div>
              </div>

              {/* Upload area */}
              <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:20, marginBottom:20 }}>
                <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:16 }}>Upload Document</p>
                <label style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', width:'100%', height:120, border:`2px dashed ${T.border2}`, borderRadius:12, cursor: docUploading?'not-allowed':'pointer', transition:'all 0.2s' }}
                  onMouseEnter={e => { if(!docUploading) e.currentTarget.style.borderColor='#3b82f6'; }}
                  onMouseLeave={e => e.currentTarget.style.borderColor=T.border2}>
                  <input type="file" style={{ display:'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls,.zip" disabled={docUploading}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setDocUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', file); fd.append('description', docDesc); fd.append('is_client_visible', String(docClientVisible));
                        const res = await fetch(`${BASE}/api/tasks/${task.id}/documents`, { method:'POST', headers:{ 'Authorization': `Bearer ${token}` }, body: fd });
                        if (res.ok) { setDocDesc(''); await fetchDocuments(); }
                        else { const d = await res.json(); alert(d.message||'Upload failed'); }
                      } catch(err) { alert('Upload error'); }
                      finally { setDocUploading(false); e.target.value=''; }
                    }} />
                  {docUploading ? (
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
                      <span style={{ width:24, height:24, border:'3px solid #3b82f6', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>
                      <p style={{ fontSize:13, color:'#3b82f6', fontWeight:600, margin:0 }}>Uploading...</p>
                    </div>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text2, margin:0 }}>Click to upload</p>
                      <p style={{ fontSize:11, color:T.text3, margin:0 }}>PDF, JPG, PNG, DOC, XLSX, ZIP · Max 10MB</p>
                    </div>
                  )}
                </label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:14 }}>
                  <div>
                    <SL>Description (optional)</SL>
                    <input type="text" placeholder="e.g. GST Registration Certificate"
                      value={docDesc} onChange={e => setDocDesc(e.target.value)} style={is} />
                  </div>
                  <div style={{ display:'flex', alignItems:'flex-end' }}>
                    <div style={{ background:T.bg3, border:`1px solid ${T.border}`, borderRadius:10, padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>
                      <div>
                        <p style={{ fontSize:13, fontWeight:500, color:T.text, margin:0 }}>Visible to Client</p>
                        <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>Show on client portal</p>
                      </div>
                      <button type="button" onClick={() => setDocClientVisible(v => !v)}
                        style={{ position:'relative', width:36, height:18, borderRadius:9, border:'none', cursor:'pointer', background: docClientVisible?'#3b82f6':(darkMode?'#334155':'#d1d5db'), transition:'background 0.2s', flexShrink:0 }}>
                        <span style={{ position:'absolute', top:2, left: docClientVisible?20:2, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document list */}
              {documents.length===0 ? (
                <div style={{ padding:'48px 0', textAlign:'center', background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}` }}>
                  <svg style={{ margin:'0 auto 12px', display:'block' }} width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No documents uploaded yet</p>
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Upload completed work files, certificates, reports</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {documents.map(doc => {
                    const EXT_COLORS = {
                      pdf:{ color:'#dc2626', bg: darkMode?'rgba(220,38,38,0.12)':'#fef2f2' },
                      jpg:{ color:'#d97706', bg: darkMode?'rgba(217,119,6,0.12)':'#fffbeb' },
                      jpeg:{ color:'#d97706', bg: darkMode?'rgba(217,119,6,0.12)':'#fffbeb' },
                      png:{ color:'#16a34a', bg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4' },
                      doc:{ color:'#2563eb', bg: darkMode?'rgba(37,99,235,0.12)':'#eff6ff' },
                      docx:{ color:'#2563eb', bg: darkMode?'rgba(37,99,235,0.12)':'#eff6ff' },
                      xlsx:{ color:'#059669', bg: darkMode?'rgba(5,150,105,0.12)':'#f0fdf4' },
                      xls:{ color:'#059669', bg: darkMode?'rgba(5,150,105,0.12)':'#f0fdf4' },
                      zip:{ color:'#7c3aed', bg: darkMode?'rgba(124,58,237,0.12)':'#f5f3ff' },
                    };
                    const ext = EXT_COLORS[doc.file_type]||{ color:T.text3, bg:T.bg3 };
                    const fileSize = doc.file_size ? doc.file_size>1024*1024 ? `${(doc.file_size/1024/1024).toFixed(1)} MB` : `${(doc.file_size/1024).toFixed(0)} KB` : '';
                    return (
                      <div key={doc.id} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:'12px 16px', display:'flex', alignItems:'center', gap:14, transition:'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                        onMouseLeave={e => e.currentTarget.style.background=T.bg2}>
                        <div style={{ width:40, height:40, borderRadius:10, background:ext.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, textTransform:'uppercase', color:ext.color, flexShrink:0 }}>
                          {doc.file_type||'FILE'}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{doc.file_name}</p>
                          <div style={{ display:'flex', alignItems:'center', gap:12, marginTop:3, flexWrap:'wrap' }}>
                            {doc.description && <span style={{ fontSize:11, color:T.text2 }}>{doc.description}</span>}
                            {fileSize && <span style={{ fontSize:11, color:T.text3 }}>{fileSize}</span>}
                            {doc.uploaded_by_name && <span style={{ fontSize:11, color:T.text3 }}>by {doc.uploaded_by_name}</span>}
                            <span style={{ fontSize:11, color:T.text3 }}>{new Date(doc.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>
                          </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                          <span style={{ fontSize:11, color:T.text3 }}>Client</span>
                          <button type="button" onClick={async () => {
                            const newVal = !doc.is_client_visible;
                            setDocuments(prev => prev.map(d => d.id===doc.id?{...d,is_client_visible:newVal}:d));
                            fetch(`${BASE}/api/tasks/${task.id}/documents/${doc.id}`,{method:'PUT',headers:{'Content-Type':'application/json',...headers},body:JSON.stringify({is_client_visible:newVal,description:doc.description})}).catch(e=>console.error(e));
                          }} style={{ position:'relative', width:30, height:16, borderRadius:8, border:'none', cursor:'pointer', background: doc.is_client_visible?'#3b82f6':(darkMode?'#334155':'#d1d5db'), transition:'background 0.2s', flexShrink:0 }}>
                            <span style={{ position:'absolute', top:2, left: doc.is_client_visible?16:2, width:12, height:12, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
                          </button>
                        </div>
                        <a href={`${BASE}${doc.file_url}`} target="_blank" rel="noreferrer" download={doc.file_name}
                          style={{ width:32, height:32, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none', transition:'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.12)':'#eff6ff'}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        </a>
                        <button type="button" onClick={() => {
                          showConfirm('Delete Document', 'Delete this document? This cannot be undone.', async () => {
                            await fetch(`${BASE}/api/tasks/${task.id}/documents/${doc.id}`,{method:'DELETE',headers}).catch(e=>console.error(e));
                            fetchDocuments();
                          });
                        }} style={{ width:32, height:32, borderRadius:8, border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* EXPENSES — task-level cost centre; rows also surface in the client's Expenses tab, tagged with this task number */}
          {activeTab==='expenses' && (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Task Expenses</h3>
                  <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>{taskExpenses.length} expense{taskExpenses.length!==1?'s':''} logged against this task</p>
                </div>
                <button type="button" onClick={() => { setEditingExpenseId(null); setShowExpenseForm(s => !s); }} style={{ padding:'8px 16px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                  + Add Expense
                </button>
              </div>

              {(() => {
                const ownExpense = taskExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);
                const billedAmount = taskInvoice?.total_amount ? Number(taskInvoice.total_amount) : null;
                const taskCount = taskInvoice?.task_count ? Number(taskInvoice.task_count) : 1;
                // When this task is billed under an invoice shared with other tasks, "Total
                // Expenses" shows the COMBINED cost of every task on that invoice (not just
                // this task's own) — same figure on every one of those tasks' pages, computed
                // fresh on each load so it's correct whether expenses were logged before or
                // after the invoice was created. Unbilled tasks just show their own cost.
                const totalExpense = (billedAmount !== null && taskInvoice?.group_expenses != null)
                  ? Number(taskInvoice.group_expenses) : ownExpense;
                const margin = billedAmount !== null ? billedAmount - totalExpense : null;
                // Billed Amount / Net Margin are profitability data — admin-only; managers & staff only see the cost total
                return (
                  <div style={{ display:'grid', gridTemplateColumns: (isAdmin && billedAmount!==null) ? 'repeat(3,1fr)' : '1fr', gap:12, marginBottom:20 }}>
                    <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:'14px 16px' }}>
                      <p style={{ fontSize:10, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Total Expenses</p>
                      <p style={{ fontSize:18, fontWeight:700, color:T.text, margin:0 }}>₹{totalExpense.toLocaleString('en-IN')}</p>
                      {billedAmount !== null && taskCount > 1 && (
                        <p style={{ fontSize:10, color:T.text3, margin:'3px 0 0' }}>Combined across {taskCount} tasks on this invoice</p>
                      )}
                    </div>
                    {isAdmin && billedAmount !== null && (
                      <>
                        <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:'14px 16px' }}>
                          <p style={{ fontSize:10, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Billed Amount</p>
                          <p style={{ fontSize:18, fontWeight:700, color:T.text, margin:0 }}>₹{billedAmount.toLocaleString('en-IN')}</p>
                        </div>
                        <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:'14px 16px' }}>
                          <p style={{ fontSize:10, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Net Margin</p>
                          <p style={{ fontSize:18, fontWeight:700, color: margin>=0?'#16a34a':'#dc2626', margin:0 }}>₹{margin.toLocaleString('en-IN')}</p>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {showExpenseForm && (
                <form onSubmit={handleSaveExpense} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:20, marginBottom:20 }}>
                  <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:16 }}>{editingExpenseId ? 'Edit Expense' : 'New Expense'}</p>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:14 }}>
                    <div>
                      <SL>Expense Date *</SL>
                      <input required type="date" value={expenseForm.expense_date} onChange={e => setExpenseForm(f => ({ ...f, expense_date: e.target.value }))} style={is} />
                    </div>
                    <div>
                      <SL>Category</SL>
                      <CategoryDropdown categories={expenseCategories} value={expenseForm.category}
                        onChange={v => setExpenseForm(f => ({ ...f, category: v }))}
                        onCreate={handleCreateCategory} darkMode={darkMode} T={T} />
                    </div>
                    <div>
                      <SL>Bank Account *</SL>
                      <BankAccountSelect accounts={bankAccounts} value={expenseForm.bank_account_id}
                        onChange={v => setExpenseForm(f => ({ ...f, bank_account_id: v }))} darkMode={darkMode} T={T} />
                    </div>
                    <div>
                      <SL>Payment Mode</SL>
                      <PaymentModeDropdown modes={paymentModes} value={expenseForm.mode}
                        onChange={v => setExpenseForm(f => ({ ...f, mode: v }))}
                        onCreate={handleCreatePaymentMode} darkMode={darkMode} T={T} />
                    </div>
                    <div>
                      <SL>Amount (₹) *</SL>
                      <input required type="number" min="0" step="0.01" placeholder="0.00" value={expenseForm.amount} onChange={e => setExpenseForm(f => ({ ...f, amount: e.target.value }))} style={is} />
                    </div>
                    <div>
                      <SL>Billing Required?</SL>
                      {expenseForm.billing_status === 'billed' ? (
                        <div style={{ padding:'9px 12px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg3, fontSize:12.5, fontWeight:600, color:'#16a34a' }}>
                          ✓ Already billed to client
                        </div>
                      ) : (
                        <div style={{ display:'flex', gap:8 }}>
                          {[['unbilled','Yes'],['non_billable','No']].map(([v,l]) => (
                            <button key={v} type="button" onClick={() => setExpenseForm(f => ({ ...f, billing_status: v }))}
                              style={{ flex:1, padding:'9px 0', borderRadius:9, fontSize:12.5, fontWeight:600, cursor:'pointer',
                                border: expenseForm.billing_status===v ? 'none' : `1px solid ${T.border2}`,
                                background: expenseForm.billing_status===v ? '#0f1f45' : 'transparent',
                                color: expenseForm.billing_status===v ? '#fff' : T.text2 }}>
                              {l}
                            </button>
                          ))}
                        </div>
                      )}
                      <p style={{ fontSize:10, color:T.text3, marginTop:5, lineHeight:1.4 }}>
                        {expenseForm.billing_status==='non_billable'
                          ? 'Borne by the firm — stays out of any client invoice.'
                          : expenseForm.billing_status==='billed' ? '' : 'Pending — shows on this client\'s invoice as the actual amount paid (government fees carry no GST).'}
                      </p>
                    </div>
                    <div>
                      <SL>Notes</SL>
                      <input type="text" placeholder="Description or remarks" value={expenseForm.notes} onChange={e => setExpenseForm(f => ({ ...f, notes: e.target.value }))} style={is} />
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                    <button type="button" onClick={resetExpenseForm} style={{ padding:'8px 16px', borderRadius:9, border:`1px solid ${T.border2}`, background:'transparent', color:T.text2, fontSize:12.5, fontWeight:600, cursor:'pointer' }}>Cancel</button>
                    <button type="submit" disabled={savingExpense} style={{ padding:'8px 18px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12.5, fontWeight:600, cursor:'pointer', opacity: savingExpense?0.6:1 }}>
                      {savingExpense ? 'Saving...' : editingExpenseId ? 'Update Expense' : 'Save Expense'}
                    </button>
                  </div>
                </form>
              )}

              {taskExpenses.length===0 && !showExpenseForm ? (
                <div style={{ padding:'48px 0', textAlign:'center', background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}` }}>
                  <svg style={{ margin:'0 auto 12px', display:'block' }} width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                  <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No expenses logged for this task</p>
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Track task-level cost to see true profitability against the billing</p>
                </div>
              ) : taskExpenses.length > 0 && (
                <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, overflow:'hidden' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                      <tr>
                        {[...['Date','Category','Mode','Amount','Billing','Notes'], ...(isAdmin ? ['Actions'] : [])].map(h => (
                          <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {taskExpenses.map((e,i) => (
                        <tr key={e.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff') }}>
                          <td style={{ padding:'10px 16px', fontSize:12.5, color:T.text2, whiteSpace:'nowrap' }}>{e.expense_date ? new Date(e.expense_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}</td>
                          <td style={{ padding:'10px 16px', fontSize:12.5, fontWeight:500, color:T.text }}>{e.category || '—'}</td>
                          <td style={{ padding:'10px 16px', fontSize:12.5, color:T.text3, textTransform:'capitalize' }}>{e.mode?.replace(/_/g,' ') || '—'}</td>
                          <td style={{ padding:'10px 16px', fontSize:12.5, fontWeight:700, color:T.text }}>₹{Number(e.amount).toLocaleString('en-IN')}</td>
                          <td style={{ padding:'10px 16px' }}>
                            <span style={{
                              fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20,
                              background: e.billing_status==='billed' ? (darkMode?'rgba(16,185,129,0.12)':'#f0fdf4')
                                : e.billing_status==='non_billable' ? (darkMode?'rgba(148,163,184,0.12)':'#f8fafc')
                                : (darkMode?'rgba(249,115,22,0.12)':'#fff7ed'),
                              color: e.billing_status==='billed' ? '#16a34a' : e.billing_status==='non_billable' ? T.text3 : '#ea580c',
                            }}>
                              {e.billing_status==='billed' ? 'Billed' : e.billing_status==='non_billable' ? 'Not Required' : 'Pending Billing'}
                            </span>
                          </td>
                          <td style={{ padding:'10px 16px', fontSize:12.5, color:T.text3, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.notes || '—'}</td>
                          {isAdmin && (
                            <td style={{ padding:'10px 16px' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                <button type="button" onClick={() => handleEditExpense(e)} title="Edit"
                                  style={{ width:26, height:26, borderRadius:7, border:'none', background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                                  onMouseEnter={ev => ev.currentTarget.style.background=darkMode?'rgba(59,130,246,0.12)':'#eff6ff'}
                                  onMouseLeave={ev => ev.currentTarget.style.background='transparent'}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                </button>
                                <button type="button" onClick={() => handleDeleteExpense(e)} title="Delete"
                                  style={{ width:26, height:26, borderRadius:7, border:'none', background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                                  onMouseEnter={ev => ev.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                                  onMouseLeave={ev => ev.currentTarget.style.background='transparent'}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
export default Tasks;